import {alpineEnabled,alpineTree,alpineForest} from '@hyperlight/shared';
import { RIVER_BANK_SURFACE_GLSL, GROUND_SURFACE_GLSL, BUTTE_SURFACE_GLSL, BUTTE_DETAIL_GAIN, CANYON_SURFACE_GLSL, CANYON_DETAIL_GAIN, DUNE_DETAIL_GAIN, hasLivingGround, groundSnow, groundBeach, groundDuneSuppression } from "./groundSurface";
import { ForestFloorIndex, buildForestFloor, type FloorAnchor } from './flora/forestFloor';
import { buildMeadowCover } from './flora/meadowCover';
import { buildRockScatter } from './flora/rockScatter';
import { canyonCanopySuppression } from './flora/canyonField';
import { getDetailTexture, groundPatchAt, GROUND_DETAIL_SCALE, GROUND_DETAIL_GLSL } from "./groundDetail";
import { landscapePalette } from "./landscapePalette";
import { mountGrassFields } from "./flora/grassBlades";
import type { GrassPatchData } from "./flora/grassPatch";
import { BasinSurface } from './water/basinSurface';
import { LavaFlows } from './water/lavaFlows';
import {makeInlandWetlandMaterial,mountInlandWetlandWater,disposeInlandWetlandWater} from './water/inlandWetlandWater';
import { VolcanicEffects,volcanicTime } from './volcano/effects';
/**
 * LOD planet: six cube-face quadtrees of worker-meshed terrain patches.
 *
 * LOD policy: a node splits when the camera is within SPLIT_FACTOR * patch
 * size of its surface center (with hysteresis to prevent thrash). Parents
 * stay visible until all four children are meshed, and children stay visible
 * until their parent is meshed on merge — the surface never has holes.
 */
import * as THREE from "three";
import { RenderCell } from '../engine/renderCells';
import { plateauRefinementHeight, butteRefinementHeight, canyonRefinementHeight, canyonFaceNeedsDetail, waterfallRefinementHeight } from './terrainRelief';
import { makePooledFloraMesh, disposeFloraMeshMaterials } from './flora/materialPool';
import {
  PlanetSampler,
  adoptCanyonCliffs,
  type CanyonCliffField,
  surfaceSupportRadius,
  surfaceSupportNormal,
  seaSurface,
  type PlanetDef,
  type TerrainSample,
  rngFromString,
  mulberry32,
  range,
  settlementInfluenceAt,
  RegionService,
  SettlementColliders,
  deserializeCityGraph,
  CITYGRAPH_VERSION,
  FACES,
  cellsPerFace,
  type InlandWetlandDef,
  type RiverPolyline,
  RIVER_BANK_FACTOR,
  dirToCell,
  packCell,
  edgeNeighbor,
  SIDE_NEG_U,
  SIDE_POS_U,
  SIDE_NEG_V,
  SIDE_POS_V,
  LEVEL_REGION,
  isCachePadDef,
  type CellId,
} from "@hyperlight/shared";
import {
  buildFlora, buildRocks, makeFloraMaterials, floraTime, floraHeight, setFloraFadeScale, type FloraSet,
  GRASS_TUNING,
  FloraLibrary, forestMix, seasonAt, maturityAt, maturityBucket, clearingAt, buildClutter, type ClutterSets,
  SPECIES, BUCKET_MATURITY,
} from "./flora";
import { deriveWaterColors, WATER_UNIFORMS_GLSL } from "./water/waterMaterial";
import { riverWaterUniforms, RIVER_SHADE_GLSL } from "./water/riverAppearance";
import { buildRiverRibbonGroup, makeRiverMaterial, RIVER_SHOW_ALT_MAX, RIVER_WRAP } from "./water/riverRibbon";
import { makeCloudShell, REBAKE_MIN_DT, type CloudUniforms, type CloudVolume } from "./weather/cloudShell";
import { makeAurora } from "./weather/aurora";
import { WeatherField, weatherTime } from "./weather/weatherField";
import { CLOUD_MAPPING_GLSL } from "./weather/cloudMapping";
import { addStylizedShading } from "./stylizedShading";
import { SEA_MAX_AMPLITUDE } from "./water/gerstner";
import { prepareScatterBounds, updateScatterLod } from "./flora/scatterLod";
import { ScatterSupport } from "./flora/scatterSupport";
import { mountWetland } from "./flora/wetland";
import type { WetlandData } from "./flora/wetlandData";
import { mountCoastalHabitat } from './flora/coastalHabitat';
import { coastalForestWeight, type CoastalData } from './flora/coastalData';
import { mountSavanna } from './flora/savanna';
import { mountInlandWetland } from './flora/inlandWetland';
import { mountRiparian } from './flora/riparian';
import { RiverRockFoam } from './water/riverRockFoam';
import type { RiparianData } from './flora/riparianData';
import { inlandWetlandForestWeight, type InlandWetlandData } from './flora/inlandWetlandData';
import { savannaForestWeight, type SavannaData } from './flora/savannaData';
import { faceDir } from "./cubesphere";
import { buildPatchIndex, type PatchData, type RegionsMsg } from "./patch";
import { RegionCache } from "./regionCache";
import type { TerrainWorkerPool } from "./workerPool";
import type { PlanetHost } from "./planetHost";
import { CanyonCliffMeshes } from './canyonCliffMeshes';

export const PATCH_RES = 41;
export const SPLIT_FACTOR = 1.8;

/**
 * The LOD level at which region residency and settlement-carve mesh gating engage — the
 * coarsest level whose vertex spacing resolves the ~16 m carve/road detail:
 *   spacing(L) = (R·π/2)/(2^L·(PATCH_RES−1)),  pick the least L with spacing ≤ 16 m,
 *   clamped to the planet's own maxLevel. Residency gates exactly the levels the
 *   settlement carve needs resolved.
 */
export function settlementLevel(radius: number, maxLevel: number): number {
  return Math.min(maxLevel, Math.ceil(Math.log2((radius * Math.PI) / (2 * 16 * (PATCH_RES - 1)))));
}

interface InlandWaterFootprint {
  face: number;
  loU: number; loV: number; hiU: number; hiV: number;
  cells: CellId[];
}

/** Finite basin bounds, clipped against each cube-face cone before projection.
 * Corner classification alone misses cells at cube seams and region corners.
 * The tangent square conservatively contains the basin's complete circular bound;
 * a linear-fractional projection has its extrema at the clipped polygon vertices.
 */
export function inlandWaterFootprints(radius: number, features: readonly Pick<InlandWetlandDef,"center"|"along"|"across"|"radiusM">[]): InlandWaterFootprint[] {
  const result: InlandWaterFootprint[] = [], n = cellsPerFace(radius, LEVEL_REGION);
  for (const feature of features) {
    const own: InlandWaterFootprint[] = [], cells: CellId[] = [];
    for (let face = 0; face < FACES.length; face++) {
      const basis = FACES[face];
      const project = (u: number, v: number, axis: readonly number[]): number =>
        (feature.center[0] * radius + feature.along[0] * u + feature.across[0] * v) * axis[0]
        + (feature.center[1] * radius + feature.along[1] * u + feature.across[1] * v) * axis[1]
        + (feature.center[2] * radius + feature.along[2] * u + feature.across[2] * v) * axis[2];
      const r = feature.radiusM;
      let polygon: [number, number][] = [[-r,-r],[r,-r],[r,r],[-r,r]];
      for (const axis of [basis.right, basis.up]) for (const sign of [-1,1]) {
        const clipped: [number, number][] = [];
        for (let i = 0; i < polygon.length; i++) {
          const a = polygon[i], b = polygon[(i+1)%polygon.length];
          const da = project(a[0],a[1],basis.normal) + sign * project(a[0],a[1],axis);
          const db = project(b[0],b[1],basis.normal) + sign * project(b[0],b[1],axis);
          if (da >= 0) clipped.push(a);
          if ((da >= 0) !== (db >= 0)) { const t = da / (da-db); clipped.push([a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t]); }
        }
        polygon = clipped;
      }
      if (polygon.length < 3) continue;
      let loU=1,loV=1,hiU=-1,hiV=-1;
      for (const [u,v] of polygon) {
        const w=project(u,v,basis.normal), cu=project(u,v,basis.right)/w, cv=project(u,v,basis.up)/w;
        loU=Math.min(loU,cu); hiU=Math.max(hiU,cu); loV=Math.min(loV,cv); hiV=Math.max(hiV,cv);
      }
      const index=(x:number)=>Math.min(n-1,Math.max(0,Math.floor((x+1)*.5*n)));
      for(let y=index(loV);y<=index(hiV);y++)for(let x=index(loU);x<=index(hiU);x++)cells.push({face,x,y,level:LEVEL_REGION});
      own.push({face,loU,loV,hiU,hiV,cells});
    }
    result.push(...own);
  }
  return result;
}

/** Conservative finite river footprint, including expanded pools (which
 * stay inside the original 2.6× carve width). Clip each segment's tangent
 * square against cube faces so seams and tiny crossed region corners survive. */
export function riverWaterRegionCells(radius:number,rivers:readonly RiverPolyline[]):CellId[]{
  const features:Pick<InlandWetlandDef,"center"|"along"|"across"|"radiusM">[]=[],seen=new Set<number>(),cells:CellId[]=[];
  for(const river of rivers)for(let i=1;i<river.points.length;i++){
    const a=river.points[i-1],b=river.points[i],center=new THREE.Vector3(a.x+b.x,a.y+b.y,a.z+b.z).normalize();
    const along=new THREE.Vector3(b.x-a.x,b.y-a.y,b.z-a.z);along.addScaledVector(center,-along.dot(center)).normalize();
    if(along.lengthSq()<.5)continue;
    const across=new THREE.Vector3().crossVectors(along,center).normalize();
    features.push({center:center.toArray(),along:along.toArray(),across:across.toArray(),radiusM:Math.max(a.half,b.half)*RIVER_BANK_FACTOR+(b.s-a.s)*.5+.2});
  }
  for(const f of inlandWaterFootprints(radius,features))for(const c of f.cells){const key=packCell(c);if(!seen.has(key)){seen.add(key);cells.push(c);}}
  return cells;
}

/**
 * Residency trigger test (pure). True when the camera is close enough to the
 * surface to warrant materializing the region cell beneath it. The trigger
 * altitude is 2× the LOD split-threshold DISTANCE at `settleLevel`, so residency
 * lands a full split-ring BEFORE any settleLevel node would request its mesh — the
 * region (and its 3×3 border neighbourhood) is resident by the time a gated node
 * asks for it, and the gate rarely has to defer.
 *
 *   nodeSize(L) = 2 / 2^L                                  (cube-face span of a level-L node)
 *   threshold   = SPLIT_FACTOR · detailQuality · nodeSize(settleLevel) · radius
 *   altitude    = |camLocal| − radius                      (height above the mean sphere)
 *   wants       ⇔ altitude < 2 · threshold
 *
 * `camLocal*` are planet-LOCAL camera coords (metres). `maxLevel` clamps
 * `settleLevel` defensively into [0, maxLevel]. Reads the module-level draw-distance
 * quality exactly as {@link Planet.updateNode} does, so widening the quality slider
 * widens the trigger in lock-step with the split policy. Deterministic given
 * (inputs + the current quality); no side effects.
 */
export function settlementTriggerWants(
  camLocalX: number,
  camLocalY: number,
  camLocalZ: number,
  radius: number,
  settleLevel: number,
  maxLevel: number,
): boolean {
  const level = Math.min(maxLevel, Math.max(0, settleLevel));
  const nodeSize = 2 / 2 ** level;
  const threshold = SPLIT_FACTOR * detailQuality * nodeSize * radius;
  const altitude = Math.hypot(camLocalX, camLocalY, camLocalZ) - radius;
  return altitude < 2 * threshold;
}

/** The subset of {@link TerrainWorkerPool} the cache-adopt path needs: broadcast a
 *  region-buffer message to every worker. Structural so a test can pass a fake. */
export interface RegionBroadcaster {
  postAll(msg: RegionsMsg): void;
}

/**
 * Apply a cache-fed region buffer to a live RegionService + worker pool (W2b-2
 * Task 3, pure/testable). Deserializes the buffer, and — unless that region is
 * ALREADY resident — {@link RegionService.adopt}s it (folding it into the carve
 * index without re-growing it) and broadcasts the buffer to every terrain worker.
 * Returns true iff it applied the region, false if it was already resident.
 *
 * WHY THE postAll IS LOAD-BEARING (the Task-2→3 FLAG): the terrain workers' region
 * push is keyed on `ensureAround`'s FRESH-cell list, which a cache-ADOPTED region
 * bypasses entirely — so if the adopt path did not do its own postAll, the workers
 * would mesh this region's tiles UNCARVED (main thread carved, workers not). The
 * broadcast here closes that gap.
 *
 * WHY THE "already resident" GUARD (the documented race + idempotence): the sync
 * `ensureAround` is the authority and may have materialized this same cell in the
 * pass between the async `cache.get` firing and its buffer landing. `adopt()` would
 * be an idempotent no-op in that case, but re-broadcasting to the workers would be
 * wasted structured clones — so we skip both and report "not applied".
 *
 * RESIDENT IS NOT THE SAME AS PRESENT (the rehydrate case): folding a region into the
 * carve index is PERMANENT, but its graph sits in a 64-entry LRU and is evicted by
 * ordinary travel. `index.has` therefore stays true for a cell whose graph is long
 * gone — and a caller that wants the GRAPH (the PoI scan reads settlements straight
 * out of it) would be told "already resident" forever while `peek` kept returning
 * null. So the guard tests for the graph too: an evicted-but-resident cell re-adopts
 * (which re-seats it in the LRU, folds nothing new, and needs no worker broadcast —
 * the workers were handed this region the first time).
 */
export function applyCachedRegion(
  service: RegionService,
  pool: RegionBroadcaster,
  planet: PlanetDef,
  buf: ArrayBuffer,
  colliders?: SettlementColliders,
): boolean {
  const g = deserializeCityGraph(buf);
  const resident = service.index.has(packCell(g.regionId));
  if (resident && service.peek(g.regionId) !== null) return false; // index AND graph: nothing to do
  service.adopt(g);
  // Fold the adopted region into settlement collision too (W2b-2.5 Task 2), the
  // same moment its carve features land in the index — idempotent per region cell.
  colliders?.addRegion(g);
  if (!resident) pool.postAll({ kind: "regions", planet, buffers: [buf] });
  return !resident;
}

/** Cloud shadows: how dark a fully-clouded patch of ground goes (albedo ×(1 − this)). */
// Resolved cloud detail, additional to the common macro cloud transmittance.
const CLOUD_SHADOW_STR = 0.22;
/** Distant-body phase: lit-side brightness multiplier on the star tint, so a moon's sunlit
 *  crescent reads bright against the night sky (the surface albedo is otherwise dim). */
const PHASE_SUN_BRIGHT = 1.6;
/** Earthshine: how lit the UNLIT (night) side of a distant body stays — so it reads as a real
 *  sphere with a visible dark side, not a crescent floating in black. (albedo × this.) */
const PHASE_EARTHSHINE = 0.15;
/** A moon deep in its planet's umbra (total lunar eclipse) dims + reddens to this coppery
 *  tone — sunlight bent through the planet's atmosphere, the classic "blood moon". Kept vivid
 *  (a stylized blood-red) rather than physically dim so the eclipsed moon still reads clearly. */
const LUNAR_COPPER = new THREE.Vector3(0.62, 0.21, 0.11);
/** 1×1 placeholder for the cloud-cover sampler until the cloud shell wires its RT in. */
const CLOUD_SHADOW_DUMMY = new THREE.DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
CLOUD_SHADOW_DUMMY.needsUpdate = true;
/** Darken the ground where the low cloud deck sits between this surface point and the sun:
 *  climb from P toward the sun to the deck shell, sample coverage there, fade with sun
 *  elevation (no shadows at night or grazing sun). P = planet-local surface position. */
const CLOUD_SHADOW_FN = /* glsl */ `
${CLOUD_MAPPING_GLSL}
uniform sampler2D uCloudCover;
uniform mat3 uCloudFrame;
uniform float uCloudR;
uniform vec3 uSunLocalT;
uniform float uCloudShadowStr;
float cloudShadow(vec3 P) {
  if (uCloudShadowStr <= 0.0) return 1.0;
  float bb = dot(P, uSunLocalT);
  float disc = bb * bb - (dot(P, P) - uCloudR * uCloudR);
  if (disc <= 0.0) return 1.0;
  vec3 cd = normalize(P + uSunLocalT * (-bb + sqrt(disc)));
  vec2 uv = cloudUv(uCloudFrame * cd);
  float cov = texture2D(uCloudCover, uv).a;
  float sunUp = smoothstep(0.0, 0.25, dot(uSunLocalT, normalize(P)));
  return 1.0 - cov * uCloudShadowStr * sunUp;
}
`;

/**
 * Target edge of a scatter batch's spatial cell, metres. Forest tiles (~500 m)
 * split 3×3, far-band tiles (~1 km) 4×4, near tiles not at all. Chosen so a
 * cell is comparable to the shadow box (400 m) and the LOD ring (280–340 m):
 * finer cells cost draw calls, coarser ones re-inflate every pass.
 */
const SCATTER_CELL_M = 170;
/** A/B hatch for perf probes, not a setting: `localStorage hl-scatter-cells=0`
 *  (+ reload) restores one batch per tile. */
const SCATTER_CELLS_OFF = typeof localStorage !== "undefined" && localStorage.getItem("hl-scatter-cells") === "0";
function scatterCells(radius: number, nodeSize: number, count: number): number {
  if (count < 32 || SCATTER_CELLS_OFF) return 1;
  const edgeM = radius * (Math.PI / 2) * (nodeSize / 2);
  return Math.min(4, Math.max(1, Math.round(edgeM / SCATTER_CELL_M)));
}

/** Draw-distance quality: scales how far out patches split to full detail.
 *  Set from the ESC menu's slider; persisted by the caller. */
let detailQuality = 1;
export function setTerrainQuality(q: number): void {
  detailQuality = Math.min(2.5, Math.max(0.6, q));
  // Scatter dissolve radii ride the same slider: rocks/grass/clutter fade
  // out (and the far tree band crossfades) in step with terrain detail.
  setFloraFadeScale(detailQuality);
}
const MERGE_HYSTERESIS = 1.2;
/** Don't issue new mesh builds when this many are already in flight. */
const MAX_QUEUE = 28;

const sharedIndex = new THREE.BufferAttribute(buildPatchIndex(PATCH_RES), 1);

type NodeState = "empty" | "building" | "ready";

/** A walk-blocking scatter prop: a cylinder along the outward radial at (x,y,z),
 *  planet-local metres — trunk-only for trees, so canopies never block. */
interface ScatterCollider {
  x: number;
  y: number;
  z: number;
  r: number;
  h: number;
  /** Curved trunks use short sections with a tight lower cap. */
  minAlong?: number;
}
/** A climbable scatter prop (boulder): a DOME rising `top` metres over its root
 *  within horizontal radius `a` — walked ON, never pushed off. */
interface ScatterStand {
  x: number;
  y: number;
  z: number;
  a: number;
  top: number;
}
/**
 * Dome height profile, as a fraction of `top`, from the squared lateral offset
 * fraction `t = (d/a)²`. Returns 0 outside.
 *
 * ⚠⚠ THIS MUST REACH ZERO WITH ZERO SLOPE AT THE RIM. The obvious profile — the
 * true ellipsoid √(1 − t) — has a VERTICAL tangent there, so the walkable height
 * a foot-step samples jumps discontinuously: measured, a 2 m boulder teleported
 * the player up 0.45 m on the frame the rim was crossed and dropped them 0.55 m
 * on the way off. In a rocky biome the domes overlap and you cross several rims a
 * second, which is exactly the reported "rocks jiggle when you walk on them".
 * Worse, the ground clamp only CATCHES a standable top within ~0.6 m of the feet
 * (character.ts `fallStep`), so a rim step taller than that was not caught at all
 * and the player walked straight THROUGH the boulder.
 *
 * The smoothstep cap fixes both: continuous height and bounded slope (peak
 * 2.23·top/a) mean the player ramps on and off every rock, and the per-frame rise
 * stays well inside the catch window even at boost speed. It also rounds the
 * crown, which reads better on a boulder than the ellipsoid's point. Cheaper too
 * — no sqrt.
 */
export const standDomeProfile = (t: number): number => (t >= 1 ? 0 : 1 - t * t * (3 - 2 * t));
const SCATTER_CELL = 24; // spatial-hash cell (m); must exceed max collider reach
const scatterKey = (x: number, y: number, z: number): string =>
  `${Math.floor(x / SCATTER_CELL)},${Math.floor(y / SCATTER_CELL)},${Math.floor(z / SCATTER_CELL)}`;

/** Legacy flora kinds → walk-blocking trunk cylinder (unit scale; × instance scale). */
const LEGACY_COLLIDE: Record<import("./flora").FloraKind, { r: number; h: number }> = {
  conifer: { r: 0.32, h: 11 },
  broadleaf: { r: 0.34, h: 8 },
  cactus: { r: 0.42, h: 6 },
  dead: { r: 0.26, h: 7 },
  crystal: { r: 0.8, h: 4 },
};

class QuadNode {
  floorRevision = -1;
  floorSignature = '';
  children: QuadNode[] | null = null;
  mesh: THREE.Mesh | null = null;
  scatter: THREE.InstancedMesh[] | null = null;
  inlandWater: THREE.Group | null = null;
  renderCell: RenderCell | null = null;
  state: NodeState = "empty";
  buildAbort: AbortController | null = null;
  /** Surface point at patch center, planet-local. */
  readonly center: THREE.Vector3;
  /** Native coastal relief can put the tile center far below a nearby brow. */
  readonly coastal: boolean;
  /** Conservative vertical range for nearby inland plateau faces. */
  readonly plateauReliefM: number;
  readonly butteReliefM: number;
  readonly canyonReliefM: number;
  readonly waterfallReliefM: number;
  /** Learned from the emitted patch, retained while its children are visible. */
  canyonFaceDetail = false;
  /** Packed LEVEL_REGION cell key under this node's center, lazily derived once by
   *  the residency mesh gate (dirToCell is scale-invariant, so center xyz works). */
  regionKey = -1;
  /** Persistent water uses final settlement clipping, even above settleLevel. */
  inlandWaterRegions: CellId[] | null = null;
  inlandWaterRegionsReady = false;

  constructor(
    readonly face: number,
    readonly level: number,
    readonly u0: number,
    readonly v0: number,
    readonly size: number,
    sampler: PlanetSampler,
  ) {
    const dir: number[] = [0, 0, 0];
    faceDir(face, u0 + size / 2, v0 + size / 2, dir);
    const r = sampler.surfaceRadius(dir[0], dir[1], dir[2]);
    this.center = new THREE.Vector3(dir[0] * r, dir[1] * r, dir[2] * r);
    this.plateauReliefM = plateauRefinementHeight(sampler,dir[0],dir[1],dir[2],size*(sampler.def?.radius??0));
    this.butteReliefM = butteRefinementHeight(sampler,dir[0],dir[1],dir[2],size*(sampler.def?.radius??0));
    this.canyonReliefM = canyonRefinementHeight(sampler,dir[0],dir[1],dir[2],size*(sampler.def?.radius??0));
    this.waterfallReliefM = waterfallRefinementHeight(sampler,dir[0],dir[1],dir[2],size*(sampler.def?.radius??0));
    this.coastal = (sampler.coastalAt?.(dir[0],dir[1],dir[2],{weight:0,heightM:0,beach:0,rock:0}).weight ?? 0) > 0;
  }
}

export class Planet implements PlanetHost {
  /** Root group, repositioned in render space every frame. */
  readonly group = new THREE.Group();
  readonly sampler: PlanetSampler;
  readonly maxLevel: number;
  /** Homestead placed-piece collision hooks (planet-local metres), assigned by
   *  main.ts when the homesteads flag is on — the character consumes them
   *  exactly like the settlement/scatter colliders (stand + wall contracts). */
  homesteadStandRadius: ((x: number, y: number, z: number) => number | null) | null = null;
  resolveHomesteadCollision: ((x: number, y: number, z: number, radius: number,
    out: { x: number; y: number; z: number }) => boolean) | null = null;
  /** Camera-boom occlusion against the same placed-piece field (see PlanetHost). */
  homesteadOccludes: ((x: number, y: number, z: number, radius: number) => boolean) | null = null;
  /** Bolt sweep against the same placed-piece field (see PlanetHost) — what stops a
   *  predicted bolt at a cache wall. */
  homesteadSegmentHit: ((ax: number, ay: number, az: number,
    bx: number, by: number, bz: number, radius: number) => number) | null = null;
  /** True once every quadtree root reports its visible area hole-free (set each update). */
  covered = false;
  /** Far sea sphere (the horizon water), or null on a dry planet. Exposed so the
   *  ocean's planar reflection can suppress it during the mirror pass. */
  readonly sea: THREE.Mesh | null = null;
  readonly basinSurface: BasinSurface | null = null;
  lavaFlows:LavaFlows|null=null;
  volcanicEffects:VolcanicEffects|null=null;
  /** Atmosphere shell, or null on an airless planet. Exposed for debug layer toggles. */
  readonly atmo: THREE.Mesh | null = null;
  private terrain = new THREE.Group();
  private roots: QuadNode[] = [];
  private material: THREE.Material;
  private wetlandMaterial: THREE.Material | null = null;
  private butteMaterial: THREE.Material | null = null;
  private wetlandButteMaterial: THREE.Material | null = null;
  private canyonMaterials: Map<number,THREE.Material> | null = null;
  private riverBankMaterials: Map<number,THREE.Material> | null = null;
  private inlandMaterials: Map<number,THREE.Material> | null = null;
  private inlandWaterFootprintCache: InlandWaterFootprint[] | null = null;
  private cliffMeshes: CanyonCliffMeshes | null = null;
  private cliffField: CanyonCliffField | null = null;
  private cliffBuild: AbortController | null = null;
  private cliffRetryAt = 0;
  private flora: FloraSet[];
  private floraLib: FloraLibrary;
  private clutter: ClutterSets;
  private maturitySeed: number;
  private clearingFreq: number;
  private floorIndex = new ForestFloorIndex<QuadNode>();
  /** Resident coastal stones, in planet-local coordinates. Water reads the
   * actual instance geometry; tile disposal also removes its water contact. */
  readonly shoreRocks = new Set<THREE.InstancedMesh>();
  private readonly riverRocks = new Set<THREE.InstancedMesh>();
  private riverRockFoam: RiverRockFoam | null = null;
  private floorNodes = new Set<QuadNode>();
  /**
   * Walk-blocking scatter props (tree trunks, stumps, boulders): radial
   * cylinders in the planet's rotating LOCAL frame, spatial-hashed by
   * SCATTER_CELL for the character's wall solve. Registered as instances
   * place, unregistered when their LOD node is destroyed.
   */
  private scatterCells = new Map<string, ScatterCollider[]>();
  // Corpse joints, outfit supports and wing corners query the same neighborhood
  // thousands of times. Cache only its keys: streamed collider buckets stay live.
  private scatterQueryCell: [number, number, number] | null = null;
  private scatterQueryKeys: string[] = [];
  private scatterRefs = new Map<QuadNode, { key: string; e: ScatterCollider }[]>();
  /** Climbable scatter (boulders): walkable DOMES that raise the ground like
   *  town roofs — the character walks up them instead of being pushed off. */
  private standCells = new Map<string, ScatterStand[]>();
  private standRefs = new Map<QuadNode, { key: string; e: ScatterStand }[]>();
  private rockGeos: THREE.BufferGeometry[];
  private pillarGeo: THREE.BufferGeometry;
  private slabGeo: THREE.BufferGeometry;
  private stoneMat: THREE.MeshLambertMaterial;
  private scatterLevel: number;
  private forestLevel: number;
  private farLevel: number;
  private disposed = false;

  /**
   * Lazy per-planet settlement region provider (W2b-2). Always constructed (cheap
   * until touched); its carve index is attached to THIS planet's main-thread sampler
   * so collision/scatter here see the same carved streets/parcels the workers mesh.
   * The LOD loop requests a 3×3 neighbourhood from a terrain worker, then adopts
   * the completed graph batch on the main thread without blocking a frame.
   */
  private regions: RegionService;
  /**
   * Solid building + retaining-wall collision for this planet's grown settlements
   * (W2b-2.5 Task 2). Shares the RegionService's carve index (attached to the
   * main-thread sampler above), and every region folded into that index is folded
   * here too at the SAME moment — the worker-build and cache-adopt paths both use
   * {@link applyCachedRegion}, so collision matches the
   * meshes the settlement renderer draws. Read via {@link settlementColliders}.
   */
  private colliders: SettlementColliders;
  /** Region-residency / mesh-gate level derived from settlement carve resolution. */
  private settleLevel: number;
  /**
   * Persistent region cache (W2b-2 Task 3). A residency batch reads cached buffers,
   * grows only its misses on a terrain worker, adopts the completed batch, and writes
   * generated buffers back under seed + cell + CITYGRAPH_VERSION.
   */
  private cache = new RegionCache();
  /** Cells currently loading from IndexedDB or growing on a terrain worker. */
  private regionCellsPending = new Set<number>();

  /** Extra split levels granted in foot mode (see update()). */
  private footLevels: number;

  /**
   * Live pose (world center + spin), written by StarSystem every frame.
   * EVERYTHING that relates a world position to this planet's terrain must
   * go through it — the ground genuinely rotates now.
   */
  readonly pose = {
    center: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    invQuat: new THREE.Quaternion(),
    spinAngle: 0,
  };
  /** Last tick's pose — the carry uses EXACT deltas, never clock math. */
  readonly posePrev = {
    center: new THREE.Vector3(),
    spinAngle: 0,
  };
  private dirTmp = new THREE.Vector3();
  private spawnLocalTmp = new THREE.Vector3();

  /**
   * The lazy per-planet region provider (W2b-2), for the settlement renderer to
   * read resident region graphs + carve index. Read-only accessor; always
   * constructed, so never null in practice (the nullable return keeps callers
   * defensive against a future planet with regions disabled).
   */
  get regionService(): RegionService | null {
    return this.regions;
  }

  /**
   * Settlement building/wall colliders for this planet (W2b-2.5 Task 2), or null if
   * regions are disabled (always constructed in practice; the nullable return keeps
   * callers — the character wall push — defensive). Kept in lock-step with the carve
   * index, so a region is collidable exactly when it is carved/rendered.
   */
  get settlementColliders(): SettlementColliders | null {
    return this.colliders;
  }

  get terrainSolids(): CanyonCliffField | null { return this.cliffField; }
  get terrainSolidsReady(): boolean { return this.cliffField !== null; }

  /** Solid support along a WORLD-space unit direction (terrain or grounded ice). */
  surfaceRadiusWorld(dirWorld: THREE.Vector3): number {
    this.dirTmp.copy(dirWorld).applyQuaternion(this.pose.invQuat);
    return surfaceSupportRadius(this.sampler, this.dirTmp.x, this.dirTmp.y, this.dirTmp.z);
  }

  /** Outward surface normal (WORLD space) along a world direction. */
  surfaceNormalWorld(dirWorld: THREE.Vector3, out: number[], eps = 0.5): void {
    this.dirTmp.copy(dirWorld).applyQuaternion(this.pose.invQuat);
    surfaceSupportNormal(this.sampler, this.dirTmp.x, this.dirTmp.y, this.dirTmp.z, out, eps);
    this.dirTmp.set(out[0], out[1], out[2]).applyQuaternion(this.pose.quat);
    out[0] = this.dirTmp.x;
    out[1] = this.dirTmp.y;
    out[2] = this.dirTmp.z;
  }

  /**
   * Request the complete settlement-carve neighbourhood under a surface spawn
   * and report whether it has landed in the main-thread sampler/colliders.
   * Spawn grounding must not sample the provisional, uncarved terrain and then
   * reveal the player after a town pad arrives at a different radius.
   */
  spawnSurfaceReady(worldPos: THREE.Vector3): boolean {
    this.spawnLocalTmp.copy(worldPos).sub(this.pose.center).applyQuaternion(this.pose.invQuat);
    const center = dirToCell(
      this.def.radius,
      LEVEL_REGION,
      this.spawnLocalTmp.x,
      this.spawnLocalTmp.y,
      this.spawnLocalTmp.z,
    );
    this.requestRegionResidency(center);
    return this.regionNeighbourhood(center).every((cell) => {
      const key = packCell(cell);
      return this.regions.index.has(key) && !this.regionCellsPending.has(key);
    });
  }

  constructor(
    readonly def: PlanetDef,
    private pool: TerrainWorkerPool,
  ) {
    this.sampler = new PlanetSampler(def);
    this.pose.center.set(def.position[0], def.position[1], def.position[2]);
    this.posePrev.center.copy(this.pose.center);
    // Leaf vertex spacing lands near ~0.8 m regardless of planet size. The cap
    // is high (12) so the large WORLD_SCALE'd planets aren't clamped to a coarse
    // mesh — they get the full crisp silhouette + fine ground (perf-heavy by
    // design; the distance-gated split policy keeps deep nodes near the camera).
    this.maxLevel = Math.min(12, Math.max(6, Math.round(Math.log2(def.radius / 20))));
    // Ground scatter (tufts/rocks/cacti) lives on leaf nodes; FORESTS live
    // four levels up, on ~250 m tiles that mesh kilometers out — trees are
    // part of the landscape, not a ring around the camera.
    this.scatterLevel = this.maxLevel;
    this.forestLevel = Math.max(3, this.maxLevel - 4);
    this.farLevel = Math.max(2, this.forestLevel - 1); // 4x-area tiles: silhouette trees ~2x the view distance
    // On foot, ~1.5 m vertex spacing visibly diverges from the analytic
    // collision surface at eye height. Dig enough extra levels to land near
    // ~0.45 m spacing; the distance-gated split policy keeps the deep nodes
    // confined to a small ring around the character.
    const leafSpacing = (def.radius * (Math.PI / 2)) / (2 ** this.maxLevel * (PATCH_RES - 1));
    this.footLevels = Math.min(3, Math.max(1, Math.ceil(Math.log2(leafSpacing / 0.45))));

    // Region residency (W2b-2): construct the lazy provider and attach its carve
    // index to this planet's main-thread sampler (attach site 1 — collision/scatter
    // on this thread now carve-match the worker meshes). settleLevel is the SAME
    // formula the town-edge refinement uses (ensureTownBands) — see settlementLevel.
    this.regions = new RegionService(this.sampler);
    this.sampler.setSettlementIndex(this.regions.index);
    // Settlement collision shares that same carve index (so buildStructures samples
    // carved ground) — every cache/worker result folds through applyCachedRegion.
    this.colliders = new SettlementColliders(this.sampler, this.regions.index);
    this.settleLevel = settlementLevel(def.radius, this.maxLevel);

    this.material = makeTerrainMaterial(def, this.sampler.amplitude);
    this.phaseU = this.material.userData.phaseU as { [k: string]: THREE.IUniform };
    for (let f = 0; f < 6; f++) {
      this.roots.push(new QuadNode(f, 0, -1, -1, 2, this.sampler));
    }
    this.group.add(this.terrain);

    // Scatter assets: a deterministic per-planet flora/rock LIBRARY (real
    // generated silhouettes, 4 variants per species — same trees on every
    // visit, for every player). What appears, and how densely, is decided
    // per node by the BIOME sampled there (see addScatter).
    this.flora = buildFlora(def);
    this.floraLib = new FloraLibrary(def);
    this.clutter = buildClutter(def);
    this.maturitySeed = Math.floor(rngFromString(`${def.seed}:maturity`)() * 2 ** 31);
    this.clearingFreq = Math.max(60, def.radius / 55); // glade noise cells ≈ 55 m of arc
    this.rockGeos = buildRocks(def);
    // Site stones: unit pillar (scaled per instance) and slab.
    this.pillarGeo = new THREE.CylinderGeometry(0.5, 0.62, 1, 6);
    this.pillarGeo.translate(0, 0.5, 0);
    this.slabGeo = new THREE.BoxGeometry(1.6, 0.5, 1.1);
    this.slabGeo.translate(0, 0.25, 0);
    const cliff = landscapePalette(def).cliff;
    this.stoneMat = new THREE.MeshLambertMaterial({
      color: new THREE.Color(cliff[0] * 0.8 + 0.18, cliff[1] * 0.8 + 0.18, cliff[2] * 0.8 + 0.2),
    });
    addStylizedShading(this.stoneMat, { rim: 0.32, rimPow: 3.0, spec: 0, weatherGround: true });

    if (def.seaRadius !== null && seaSurface(def) !== 'water') {
      this.basinSurface = new BasinSurface(def);
      (this as { sea: THREE.Mesh | null }).sea = this.basinSurface.far;
      this.group.add(this.basinSurface.far);
    } else if (def.seaRadius !== null) {
      // Sink the far sphere just below the animated patch's deepest trough so
      // wave troughs never reveal the flat orb, and colour it to the patch's
      // DEEP tone so beyond the patch rim the two are indistinguishable.
      const deep = deriveWaterColors(def.seaColor).deep;
      // Ocean-from-orbit tone: a bright vivid blue carrying a little of this sea's own hue, so
      // water worlds read as blue marbles from space (not the dark close-up "deep" tone).
      const sc = def.seaColor;
      const space = new THREE.Color(sc[0] * 0.25 + 0.04, sc[1] * 0.45 + 0.12, sc[2] * 0.5 + 0.42);
      const sea = new THREE.Mesh(
        new THREE.SphereGeometry(def.seaRadius - (SEA_MAX_AMPLITUDE + 0.8), 96, 64),
        makeSeaMaterial(
          new THREE.Color(deep.x, deep.y, deep.z),
          space,
          this.material.userData.cloudShadowU as { [k: string]: THREE.IUniform } | null,
          this.phaseU,
        ),
      );
      (this as { sea: THREE.Mesh | null }).sea = sea;
      this.group.add(sea);
    }

    if (def.ring) {
      this.group.add(makeRing(def));
    }

    if (def.atmosphere) {
      const atmo = makeAtmosphere(def.radius, def.atmosphere.height, def.atmosphere.color);
      this.atmoCam = atmo.material.uniforms.uCam.value as THREE.Vector3;
      this.atmoDay = atmo.material.uniforms.uDay as { value: number };
      this.atmoFade = atmo.material.uniforms.uFade as { value: number };
      // Golden-hour: drive the shell's colour each frame between its base hue and a
      // warm sunset tint (mostly SUNSET_WARM, carrying a little atmosphere hue).
      const ac = def.atmosphere.color;
      this.atmoColorU = atmo.material.uniforms.uColor.value as THREE.Vector3;
      this.atmoBaseColor = this.atmoColorU.clone();
      this.atmoSunsetColor = new THREE.Vector3(
        SUNSET_WARM[0] * 0.7 + ac[0] * 0.3,
        SUNSET_WARM[1] * 0.7 + ac[1] * 0.3,
        SUNSET_WARM[2] * 0.7 + ac[2] * 0.3,
      );
      (this as { atmo: THREE.Mesh | null }).atmo = atmo;
      this.group.add(atmo);
      // Volumetric cloud layer — baked offscreen, displayed cheaply (air worlds only).
      this.weatherField = new WeatherField(def, this.sampler);
      const cloud = makeCloudShell(def.radius, def.atmosphere.height, ac, rngFromString(def.seed + ":cloud")() * 100, this.weatherField);
      this.cloudU = cloud.uniforms;
      this.cloudVolume = cloud.volume;
      this.cloudBake = cloud.bake;
      this.cloudUpdate = cloud.update;
      this.cloudDispose = cloud.dispose;
      this.group.add(cloud.mesh);
      this.aurora = makeAurora(def);
      this.group.add(this.aurora.mesh);
      // Wire the low deck's coverage into the terrain shader so clouds shadow the ground.
      const csu = this.material.userData.cloudShadowU as { [k: string]: THREE.IUniform } | null;
      if (csu) {
        csu.uCloudCover.value = cloud.shadow.tex;
        csu.uCloudR.value = cloud.shadow.radius;
        csu.uCloudFrame.value = cloud.shadow.frame;
        csu.uCloudShadowStr.value = CLOUD_SHADOW_STR;
        this.cloudShadowU = csu;
      }
    }
  }

  private atmoCam: THREE.Vector3 | null = null;
  weatherField: WeatherField | null = null;
  private atmoDay: { value: number } | null = null;
  private atmoFade: { value: number } | null = null;
  private atmoColorU: THREE.Vector3 | null = null;
  private readonly atmoBaseColor = new THREE.Vector3();
  private readonly atmoSunsetColor = new THREE.Vector3();
  private cloudU: CloudUniforms | null = null;
  private aurora: ReturnType<typeof makeAurora> | null = null;
  private readonly phaseU: { [k: string]: THREE.IUniform };
  private cloudVolume: CloudVolume | null = null;
  private cloudShadowU: { [k: string]: THREE.IUniform } | null = null;
  private cloudBake: ((renderer: THREE.WebGLRenderer, time: number) => void) | null = null;
  private cloudUpdate: ((time: number) => void) | null = null;
  private cloudDispose: (() => void) | null = null;
  private cloudBakedOnce = false;
  private cloudBakeAt = 0;
  private readonly _camLocal = new THREE.Vector3();
  get cloudImmersion(): number { return this.cloudVolume?.uEdgeFade.value ?? 0; }

  /** Common near-view sky tint and real lunar illumination, set after sky evaluation. */
  setWeatherLighting(tint: THREE.Color, moonDirection: THREE.Vector3, moonLight: THREE.Color, day?: number, sunVisibility=1): void {
    this.atmoColorU?.set(tint.r, tint.g, tint.b);
    if (this.cloudU) {
      if (day !== undefined) this.cloudU.uDay.value = day;
      this.cloudU.uSunVisibility.value=sunVisibility;
      this.cloudU.uMoonLocal.value.copy(moonDirection).applyQuaternion(this.pose.invQuat);
      this.cloudU.uMoonLight.value.set(moonLight.r, moonLight.g, moonLight.b);
    }
  }

  /**
   * Per frame: the atmosphere shader ray-marches in planet-local space and
   * needs the camera position there (the group is never rotated, so this is
   * just a subtraction in render space). `day` is THIS planet's day factor
   * for the viewer's side — the shell glow dies on night sides.
   */
  updateAtmosphereCam(
    camRenderPos: THREE.Vector3,
    day = 1,
    fade = 1,
    sunset = 0,
    starCol?: THREE.Color,
    lunarShadow = 0,
  ): void {
    // Distant-body PHASE: own sun direction (planet-local, toward the star) + a brightness
    // that ramps in with viewing distance so the surface you stand on keeps normal lighting
    // (uPhaseLit 0) and a sky body shows a true, full-brightness phase (uPhaseLit 1).
    const ph = this.phaseU;
    (ph.uPhaseSun.value as THREE.Vector3).copy(this.pose.center).normalize().negate().applyQuaternion(this.pose.invQuat);
    const bodyDist = camRenderPos.distanceTo(this.group.position);
    ph.uPhaseLit.value = smoothstep01(this.def.radius * 2.5, this.def.radius * 8.0, bodyDist);
    if (starCol)
      (ph.uPhaseColor.value as THREE.Vector3).set(starCol.r, starCol.g, starCol.b).multiplyScalar(PHASE_SUN_BRIGHT);
    // Lunar eclipse: when this body sits in its parent's umbra, dim + redden the lit side
    // toward a coppery "blood moon" (sunlight bent through the parent's atmosphere).
    (ph.uPhaseEclipse.value as THREE.Vector3).set(1, 1, 1).lerp(LUNAR_COPPER, lunarShadow);
    // Camera in planet-LOCAL space (the group both translates AND rotates now). Shared by
    // the atmosphere shell + the near-field cloud volume, both of which ray-march there.
    const camLocal = this._camLocal.copy(camRenderPos).sub(this.group.position).applyQuaternion(this.pose.invQuat);
    this.atmoCam?.copy(camLocal);
    // Distant atmospheric limbs belong behind the viewer's clouds. The local
    // sky haze still composites over its own decks. Depth partitions alone do
    // not separate a nearby moon from the local sky shell.
    if (this.atmo && this.def.atmosphere) this.atmo.renderOrder = atmosphereRenderOrder(this.def.radius, this.def.atmosphere.height, bodyDist);
    if (this.atmoDay) this.atmoDay.value = day;
    if (this.atmoFade) this.atmoFade.value = fade;
    // Warm the haze toward this planet's sunset tint as the sun grazes the horizon.
    this.atmoColorU?.lerpVectors(this.atmoBaseColor, this.atmoSunsetColor, sunset);
    // Clouds: continuous epoch transport plus planet-local light inputs for the
    // next atlas bake. Geometry, shadow coverage and the flight volume agree.
    if (this.cloudU) {
      this.cloudUpdate?.(weatherTime());
      this.cloudU.uDay.value = day;
      this.cloudU.uSunset.value = sunset;
      this.cloudU.uSunVisibility.value=1-lunarShadow*.85;
      // Direction TO the sun (star at system 0), planet-local. copy() FIRST — normalize
      // mutates in place, and pose.center must not be touched.
      this.cloudU.uSunLocal.value.copy(this.pose.center).normalize().negate().applyQuaternion(this.pose.invQuat);
      this.aurora?.update(weatherTime(),camLocal,this.cloudU.uSunLocal.value);
    }
    // Cloud shadows on the terrain track the same planet-local sun direction.
    if (this.cloudShadowU && this.cloudU) {
      this.cloudShadowU.uSunLocalT.value.copy(this.cloudU.uSunLocal.value);
    }
    // Near-field cloud volume: feed it the camera in local space, and RAMP it in over
    // altitude (never a hard toggle — that pops the fog in on atmosphere entry). It fades
    // 0→1 across the lower band edge and 1→0 across the upper one, so descending from space
    // the volume eases in as you reach the decks. surfaceDist = |camLocal| − radius.
    if (this.cloudVolume) {
      const v = this.cloudVolume;
      v.uCamLocal.value.copy(camLocal);
      const surfaceDist = camLocal.length() - this.def.radius;
      const m = v.fadeMargin;
      const fadeIn = smoothstep01(v.bandLo - m, v.bandLo + m, surfaceDist);
      const fadeOut = 1 - smoothstep01(v.bandHi - m, v.bandHi + m, surfaceDist);
      const fade = Math.min(fadeIn, fadeOut);
      v.uEdgeFade.value = fade;
      v.mesh.visible = fade > 0.001; // skip the march entirely when fully faded out
    }
  }

  /** The low cloud deck's coverage, for shadowing OTHER surfaces (the ocean patch).
   *  null on airless worlds. The sun direction is sampled per-surface by the caller. */
  getCloudShadow(): { tex: THREE.Texture; radius: number; str: number; frame: THREE.Matrix3 } | null {
    if (!this.cloudShadowU) return null;
    return {
      tex: this.cloudShadowU.uCloudCover.value as THREE.Texture,
      radius: this.cloudShadowU.uCloudR.value as number,
      str: this.cloudShadowU.uCloudShadowStr.value as number,
      frame: this.cloudShadowU.uCloudFrame.value as THREE.Matrix3,
    };
  }

  /**
   * Render this planet's cloud field into its offscreen target. Every air world bakes
   * once on arrival and periodically thereafter; the viewer's planet updates more
   * often. Returns true if baked, allowing distant updates to be staggered.
   * `time` is a small session-local clock (NOT the epoch — that overflows the noise).
   */
  bakeClouds(renderer: THREE.WebGLRenderer, time: number, isNear: boolean): boolean {
    if (!this.cloudBake) return false;
    if (this.cloudBakedOnce) {
      if (time >= this.cloudBakeAt && time - this.cloudBakeAt < (isNear ? REBAKE_MIN_DT : 20)) return false;
    }
    this.cloudBake(renderer, time);
    this.cloudBakedOnce = true;
    this.cloudBakeAt = time;
    return true;
  }

  /** Terrain surface radius along a unit direction (for collision/altitude). */
  surfaceRadius(dir: THREE.Vector3): number {
    return surfaceSupportRadius(this.sampler, dir.x, dir.y, dir.z);
  }

  /** River water-surface ribbons (P1 flowing rivers) — built lazily on the
   *  first near-planet updateWater() call, parented to `group` so they ride
   *  orbit + spin. Null until built (or when the planet has no rivers). */
  private riverGroup: THREE.Group | null = null;
  private riverMat: THREE.ShaderMaterial | null = null;
  private inlandWaterGroup: THREE.Group | null = null;
  private inlandWaterMat: THREE.ShaderMaterial | null = null;
  get inlandWetlandWaterGroup():THREE.Group|null{return this.inlandWaterGroup;}
  get inlandWetlandWaterMaterial():THREE.ShaderMaterial|null{return this.inlandWaterMat;}
  get riverWaterGroup(): THREE.Group | null { return this.riverGroup; }
  get riverWaterMaterial(): THREE.ShaderMaterial | null { return this.riverMat; }
  private riverBuildTried = false;
  private riverRegionCells:CellId[]|null=null;
  private riverRegionsReady=false;
  get riverWaterReady():boolean{return this.riverBuildTried;}

  /** River shore geometry must see final settlement carving across its finite
   * footprint. Cache/worker residency is shared with terrain, and survives LRU
   * graph eviction. Once resident, no river rebuild or per-frame sampling occurs. */
  private riverWaterResident():boolean {
    if(this.riverRegionsReady)return true;
    if(!this.riverRegionCells){
      const seen=new Set<number>(),cells:CellId[]=[];
      for(const cell of riverWaterRegionCells(this.def.radius,this.sampler.rivers().rivers))for(const neighbour of this.regionNeighbourhood(cell)){
        const key=packCell(neighbour);if(!seen.has(key)){seen.add(key);cells.push(neighbour);}
      }
      this.riverRegionCells=cells;
    }
    if(this.riverRegionCells.every(cell=>this.regions.index.has(packCell(cell)))){this.riverRegionsReady=true;return true;}
    this.requestRegionCellsResidency(this.riverRegionCells);return false;
  }

  /**
   * Advance the river flow clock and match daylight/sun on the terrain material
   * (the painted far channel) AND the river ribbon surfaces. Cosmetic; call near
   * foot. `sunDir`/`sunColor` are render-space, like the ocean patch uses.
   * `camPos` (render space) gates the ribbon group by altitude, ocean-patch style.
   */
  updateWater(time: number, sunDir: THREE.Vector3, sunColor: THREE.Color, dayLight: number, camPos?: THREE.Vector3): void {
    this.basinSurface?.setTime(time);
    this.lavaFlows?.setTime(time);
    this.lavaFlows?.setActivity(volcanicTime(time));
    this.volcanicEffects?.setTime(time);
    const wu = this.material.userData.waterUniforms as { [k: string]: THREE.IUniform } | undefined;
    if (wu) {
      // Epoch seconds are ~1.78e9 — float32 ULP there is ~128 s, so an unwrapped
      // uTime froze the ripple phase entirely (same trap as the fountains' WATER_TIME
      // and the ocean's TIME_WRAP). Wrap on a whole number of ripple periods so the
      // wrap itself is phase-seamless.
      wu.uTime.value = time % RIVER_TIME_WRAP;
      (wu.uSunDir.value as THREE.Vector3).copy(sunDir);
      (wu.uSunColor.value as THREE.Vector3).set(sunColor.r, sunColor.g, sunColor.b);
      wu.uDayLight.value = dayLight;
    }

    // --- River ribbons: the actual water surface at ws (P1 flowing rivers) ---
    if (!this.riverBuildTried && this.riverWaterResident()) {
      this.riverBuildTried = true;
      if (this.def.seaRadius !== null && this.def.terrain.riverDensity > 0 && this.sampler.rivers().rivers.length > 0) {
        this.riverMat = makeRiverMaterial(this.def, getDetailTexture());
        this.riverGroup = buildRiverRibbonGroup(this.sampler, this.def, this.riverMat);
        this.riverRockFoam=new RiverRockFoam(this.sampler,this.riverMat);
        this.riverGroup.add(this.riverRockFoam.group);
        for(const rocks of this.riverRocks)this.riverRockFoam.add(rocks);
        this.group.add(this.riverGroup);
      }
    }
    this.riverRockFoam?.updateVisibility();
    for(const [group,material] of [[this.riverGroup,this.riverMat],[this.inlandWaterGroup,this.inlandWaterMat]] as const){
      if(!group||!material)continue;
      group.visible=!camPos||camPos.distanceTo(this.pose.center)-this.def.radius<RIVER_SHOW_ALT_MAX;
      if(!group.visible)continue;
      const ru=material.uniforms;
      ru.uTime.value=time%RIVER_WRAP;
      (ru.uSunDir.value as THREE.Vector3).copy(sunDir);
      (ru.uSunColor.value as THREE.Vector3).set(sunColor.r,sunColor.g,sunColor.b);
      ru.uDayLight.value=dayLight;
      (ru.uCenter.value as THREE.Vector3).copy(this.group.position);
    }
  }

  private lodCap = 0;
  private readonly scatterEye = new THREE.Vector3();

  /**
   * Refresh the LOD tree. `camLocal` is the camera position in planet-local
   * space. Throttled by the caller — traversal is cheap, meshing is not.
   * `onFoot` unlocks the extra near-ground levels for a walking character.
   */
  update(camLocal: THREE.Vector3, onFoot = false): void {
    if (this.disposed) return;
    if (!this.cliffField && !this.cliffBuild && performance.now()>=this.cliffRetryAt && camLocal.length()-this.def.radius<9000) {
      const build=this.cliffBuild=new AbortController();
      void this.pool.buildCanyonCliffs(this.def,build.signal).then(solids=>{
        if(this.disposed||this.cliffBuild!==build)return;
        if(!solids){this.cliffBuild=null;this.cliffRetryAt=performance.now()+1000;return;}
        const field=adoptCanyonCliffs(this.sampler,solids);
        const variants=this.canyonMaterials??=new Map<number,THREE.Material>();
        let material=variants.get(0);
        if(!material){material=makeTerrainMaterial(this.def,this.sampler.amplitude,false,this.material,false,true);variants.set(0,material);}
        const meshes=new CanyonCliffMeshes(this.sampler,field,material);
        // Commit physics and visible geometry together. Query availability and
        // ownership never follow individual heightfield LOD children.
        this.group.add(meshes.group);this.cliffMeshes=meshes;this.cliffField=field;this.cliffBuild=null;
      }).catch(error=>{
        if(this.disposed||this.cliffBuild!==build)return;
        this.cliffBuild=null;this.cliffRetryAt=performance.now()+2000;
        console.error('Canyon cliff adoption failed:',error);
      });
    }
    this.basinSurface?.update(camLocal);
    if(this.def.archetype==='volcanic') {
      const near=camLocal.length()-this.def.radius<9000;
      if(near&&!this.lavaFlows){this.lavaFlows=new LavaFlows(this.sampler);this.group.add(this.lavaFlows.group);}
      if(near&&!this.volcanicEffects){this.volcanicEffects=new VolcanicEffects(this.sampler);this.group.add(this.volcanicEffects.group);}
      if(this.lavaFlows)this.lavaFlows.group.visible=near;
      if(this.volcanicEffects)this.volcanicEffects.group.visible=near;
    }
    this.scatterEye.copy(camLocal);
    this.lodCap = this.maxLevel + (onFoot ? this.footLevels : 0);
    // Region residency (W2b-2): near the surface, request the current 3×3 border
    // neighbourhood (so cross-border carves are correct). Cache hits and worker
    // builds land asynchronously; updateNode keeps deep meshes gated on residency
    // and renders the coarse parent until the complete graph batch is adopted.
    if (settlementTriggerWants(camLocal.x, camLocal.y, camLocal.z, this.def.radius, this.settleLevel, this.maxLevel)) {
      const cell = dirToCell(this.def.radius, LEVEL_REGION, camLocal.x, camLocal.y, camLocal.z);
      this.requestRegionResidency(cell);
    }
    // Secure a complete coarse surface before recursive detail requests fill
    // MAX_QUEUE. On a cold arrival, depth-first traversal could otherwise keep
    // entire later faces unmeshed for seconds while refining the first face.
    for (const root of this.roots) this.requestMesh(root);
    let covered = true;
    for (const root of this.roots) covered = this.updateNode(root, camLocal) && covered;
    this.refreshForestFloor();
    this.covered = covered && this.cliffBuild===null;
  }

  /** Returns true if the node's area is fully covered by visible meshes. */
  private updateNode(node: QuadNode, cam: THREE.Vector3): boolean {
    let dist = cam.distanceTo(node.center);
    let canyonFoot = false;
    const reliefHeight=Math.max(node.coastal?220:0,node.plateauReliefM??0,node.butteReliefM??0,node.canyonReliefM??0,node.waterfallReliefM??0);
    if(reliefHeight>0 && this.lodCap>this.maxLevel){
      // At a cliff brow, center-distance alone keeps the adjacent face coarse
      // because its center is far below the observer. Refine the nearby radial
      // footprint using the existing foot-level cap, with a bounded height fade.
      const r=node.center.length(),projected=cam.dot(node.center)/r;
      const height=Math.abs(projected-r);
      if(height<reliefHeight){
        const lateral=Math.sqrt(Math.max(0,cam.lengthSq()-projected*projected));
        dist=Math.min(dist,lateral+Math.max(0,height-(reliefHeight-60))*3);
        canyonFoot=node.canyonFaceDetail===true && (node.canyonReliefM??0)>0 && height<node.canyonReliefM;
      }
    }
    let threshold = SPLIT_FACTOR * detailQuality * node.size * this.def.radius;
    // Fracture corners need one finer physical surface than the ordinary
    // foot cap. A completed parent must prove exposed steep rock; flat wash
    // floors keep their ordinary cap. Until data arrives the normal fallback
    // stays complete, then refinement can follow on the next traversal. The
    // vertical fade and merge hysteresis still bound the extra work.
    if(canyonFoot && node.level>=this.maxLevel)threshold=Math.max(threshold,80);
    if (node.children) threshold *= MERGE_HYSTERESIS; // hysteresis: harder to merge than to split
    const wantSplit = node.level < this.lodCap+(canyonFoot?1:0) && dist < threshold;

    if (wantSplit) {
      // These parent levels own persistent vegetation/props even when finer
      // children supply every visible terrain triangle. Request their data
      // before descending: fast children must not bypass scatter ownership.
      if (node.level === this.farLevel || node.level === this.forestLevel
        || node.level === this.scatterLevel - 2 || node.level === this.scatterLevel) this.requestMesh(node);
      if (!node.children) node.children = this.createChildren(node);
      let covered = true;
      for (const c of node.children) covered = this.updateNode(c, cam) && covered;
      if (covered) {
        this.setVisible(node, false);
        // Foot-mode levels dig BELOW the scatter level; the children only
        // refine the terrain mesh, so this node's rocks/trees must stay.
        if (node.scatter) for (const s of node.scatter) updateScatterLod(s, this.scatterEye, true);
        if(node.inlandWater)node.inlandWater.visible=true;
        return true;
      }
      // Children still meshing: keep showing this node as the fallback.
      this.requestMesh(node);
      const ok = node.state === "ready";
      this.setVisible(node, ok);
      // Child traversal may have exposed a partially ready frontier. A coarse
      // fallback covers ALL four quadrants, so none of those descendants may
      // draw over it (their nearly coplanar triangles flicker while walking).
      if (ok) for (const child of node.children) this.hideSubtree(child);
      return ok;
    }

    this.requestMesh(node);
    if (node.state === "ready") {
      this.setVisible(node, true);
      if (node.children) {
        for (const c of node.children) this.destroyNode(c);
        node.children = null;
      }
      return true;
    }
    // Not meshed yet — restore available child coverage. An ancestor fallback
    // may have hidden it last pass, so visibility is not a readiness test.
    return this.showAvailableSubtree(node);
  }

  private hideSubtree(node: QuadNode): void {
    this.setVisible(node, false);
    if (node.children) for (const child of node.children) this.hideSubtree(child);
  }

  /** Coarsest available, non-overlapping cover while a merge mesh is pending. */
  private showAvailableSubtree(node: QuadNode): boolean {
    const ready = node.state === "ready" && node.mesh !== null;
    this.setVisible(node, ready);
    if (ready) {
      if (node.children) for (const child of node.children) this.hideSubtree(child);
      return true;
    }
    if (!node.children) return false;
    let covered = true;
    for (const child of node.children) covered = this.showAvailableSubtree(child) && covered;
    if (covered && node.scatter) for (const s of node.scatter) updateScatterLod(s, this.scatterEye, true);
    if(covered&&node.inlandWater)node.inlandWater.visible=true;
    return covered;
  }

  private createChildren(node: QuadNode): QuadNode[] {
    const s = node.size / 2;
    const out: QuadNode[] = [];
    for (let j = 0; j < 2; j++) {
      for (let i = 0; i < 2; i++) {
        out.push(
          new QuadNode(node.face, node.level + 1, node.u0 + i * s, node.v0 + j * s, s, this.sampler),
        );
      }
    }
    return out;
  }

  /**
   * Is the region cell under this node's center resident in the carve index?
   * Caches the packed cell key on the node (dirToCell is scale-invariant, so the
   * center's planet-local xyz classify the same as its unit direction).
   */
  private regionResident(node: QuadNode): boolean {
    if (node.regionKey < 0) {
      const c = node.center;
      node.regionKey = packCell(dirToCell(this.def.radius, LEVEL_REGION, c.x, c.y, c.z));
    }
    return this.regions.index.has(node.regionKey);
  }

  private regionNeighbourhood(center: CellId): CellId[] {
    const R = this.def.radius;
    const negU = edgeNeighbor(R, center, SIDE_NEG_U);
    const posU = edgeNeighbor(R, center, SIDE_POS_U);
    const negV = edgeNeighbor(R, center, SIDE_NEG_V);
    const posV = edgeNeighbor(R, center, SIDE_POS_V);
    const cells: CellId[] = [
      center, negU, posU, negV, posV,
      edgeNeighbor(R, negU, SIDE_NEG_V), edgeNeighbor(R, negU, SIDE_POS_V),
      edgeNeighbor(R, posU, SIDE_NEG_V), edgeNeighbor(R, posU, SIDE_POS_V),
    ];
    const seen = new Set<number>();
    return cells.filter((cell) => { const key = packCell(cell); if (seen.has(key)) return false; seen.add(key); return true; });
  }

  /**
   * Resolve cache hits and grow misses off-thread, then adopt the complete batch.
   * Deep terrain nodes remain gated by regionResident until this promise lands,
   * so players see the coarse parent briefly instead of a multi-hundred-ms frame.
   */
  private requestRegionResidency(center: CellId): void {
    this.requestRegionCellsResidency(this.regionNeighbourhood(center));
  }

  private requestRegionCellsResidency(want: readonly CellId[]): void {
    const cells = want.filter((cell) => {
      const key = packCell(cell);
      if (this.regions.index.has(key) || this.regionCellsPending.has(key)) return false;
      this.regionCellsPending.add(key);
      return true;
    });
    if (cells.length === 0) return;
    void this.loadRegionBatch(cells);
  }

  /** Water parents stay mounted below fine terrain. Wait for every region that
   * can carve the actual basin, not just the far tile's centre or camera region.
   * Offscreen owners request their own finite neighbourhood, so they cannot wait
   * forever for a camera visit. Index residency persists across graph eviction.
   */
  private inlandWaterResident(node: QuadNode): boolean {
    if (node.inlandWaterRegionsReady) return true;
    if (!node.inlandWaterRegions) {
      const footprints = this.inlandWaterFootprintCache ??= inlandWaterFootprints(this.def.radius,this.sampler.inlandWetlands?.() ?? []);
      const seen = new Set<number>(), cells: CellId[] = [];
      for (const f of footprints) {
        if (f.face!==node.face || f.hiU<node.u0 || f.loU>node.u0+node.size || f.hiV<node.v0 || f.loV>node.v0+node.size) continue;
        for (const cell of f.cells) for (const neighbour of this.regionNeighbourhood(cell)) {
          const key=packCell(neighbour); if(seen.has(key))continue; seen.add(key); cells.push(neighbour);
        }
      }
      node.inlandWaterRegions = cells;
    }
    if (node.inlandWaterRegions.every(cell=>this.regions.index.has(packCell(cell)))) {
      node.inlandWaterRegionsReady = true;
      return true;
    }
    this.requestRegionCellsResidency(node.inlandWaterRegions);
    return false;
  }

  /**
   * Make specific region cells resident — cache-first, worker-grown, off-thread,
   * exactly like the LOD's own residency batch (which only ever asks for the 3×3
   * under the camera). The PoI scan uses this to KNOW a settlement it can see the
   * seed of but has not flown to: without it, the scan can only report the towns
   * the renderer happened to stream, which is why a player who spawned and never
   * left had an empty overlay. Deduped against pending + already-resident, so
   * calling it every frame with the same cells costs one Set lookup each.
   */
  requestRegions(cells: CellId[]): void {
    const want = cells.filter((cell) => {
      const key = packCell(cell);
      // Gate on the GRAPH, not on index residency: this caller wants to READ the
      // region, and a cell can be resident with its graph long since LRU-evicted.
      if (this.regions.peek(cell) !== null || this.regionCellsPending.has(key)) return false;
      this.regionCellsPending.add(key);
      return true;
    });
    if (want.length === 0) return;
    void this.loadRegionBatch(want);
  }

  private async loadRegionBatch(cells: CellId[]): Promise<void> {
    try {
      const cached = await Promise.all(cells.map((cell) =>
        this.cache.get(this.def.seed, packCell(cell), CITYGRAPH_VERSION).catch(() => null)));
      if (this.disposed) return;
      // Validate cached bytes before deciding which pure graph builds are misses.
      for (let i = 0; i < cached.length; i++) if (cached[i]) {
        try {
          const graph = deserializeCityGraph(cached[i]!);
          if (packCell(graph.regionId) !== packCell(cells[i])) cached[i] = null;
        } catch { cached[i] = null; }
      }
      // A cache MISS still needs growing when we hold no graph for the cell. Gate on
      // the graph rather than on index residency: the index keeps a cell "resident"
      // forever, but its graph is LRU-evictable, and a reader (the PoI scan) that asks
      // for an evicted region must get it back — not be told it already has it.
      const missing = cells.filter((cell, index) => cached[index] === null && this.regions.peek(cell) === null);
      const generated = missing.length > 0 ? await this.pool.buildRegions(this.def, missing) : [];
      if (this.disposed) return;
      const generatedByKey = new Map<number, ArrayBuffer>();
      for (let i = 0; i < missing.length; i++) generatedByKey.set(packCell(missing[i]), generated[i]);
      for (let i = 0; i < cells.length; i++) {
        const key = packCell(cells[i]);
        const generatedBuffer = generatedByKey.get(key);
        const buffer = cached[i] ?? generatedBuffer;
        if (!buffer || this.regions.peek(cells[i]) !== null) continue;
        applyCachedRegion(this.regions, this.pool, this.def, buffer, this.colliders);
        // Persist whatever we had to GROW — a cache hit is on disk already. This
        // fires on a rehydrate too: applyCachedRegion reports "nothing newly
        // resident" there, but the bytes we just paid for are worth keeping.
        if (generatedBuffer) void this.cache.put(this.def.seed, key, CITYGRAPH_VERSION, buffer);
      }
    } finally {
      for (const cell of cells) this.regionCellsPending.delete(packCell(cell));
    }
  }

  private requestMesh(node: QuadNode): void {
    if (node.state !== "empty") return;
    const inlandWater = node.level === this.farLevel && hasLivingGround(this.def) && !isCachePadDef(this.def);
    if (inlandWater && !this.inlandWaterResident(node)) return;
    // Region-residency gate (W2b-2): a node deep enough to resolve settlement carve
    // (level ≥ settleLevel) defers meshing until its region cell is resident, so it
    // never meshes an un-carved surface that would pop when the region lands. Natural
    // retry — the next LOD pass re-visits it (residency fires from update() first).
    if (node.level >= this.settleLevel && !this.regionResident(node)) return;
    if (this.pool.queueDepth >= MAX_QUEUE) return; // try again next LOD pass
    node.state = "building";
    const build = node.buildAbort = new AbortController();
    this.pool
      .build({
        planet: this.def,
        face: node.face,
        level: node.level,
        u0: node.u0,
        v0: node.v0,
        size: node.size,
        res: PATCH_RES,
        wetland: node.level === this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        coastal: node.level === this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        coastalFar: node.level === this.farLevel && this.farLevel !== this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        savanna: node.level === this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        inlandWater,
        riparian: node.level === this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        inlandWetland: node.level === this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        inlandWetlandFar: node.level === this.farLevel && this.farLevel !== this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        savannaFar: node.level === this.farLevel && this.farLevel !== this.forestLevel && hasLivingGround(this.def) && !isCachePadDef(this.def),
        grass: node.level === this.scatterLevel && this.def.hasFlora && !isCachePadDef(this.def)
          ? { density: GRASS_TUNING.density, height: GRASS_TUNING.height } : undefined,
      }, build.signal)
      .then((data) => {
        if (node.buildAbort !== build || !data) return;
        node.buildAbort = null;
        this.onPatchBuilt(node, data);
      });
  }

  private onPatchBuilt(node: QuadNode, data: PatchData): void {
    if (this.disposed || node.state !== "building") return;
    node.canyonFaceDetail=canyonFaceNeedsDetail(data.slopes,data.canyonSurface);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
    geo.setAttribute("normal", new THREE.BufferAttribute(data.normals, 3));
    geo.setAttribute("field", new THREE.BufferAttribute(data.fields, 4));
    geo.setAttribute("slope", new THREE.BufferAttribute(data.slopes, 1));
    geo.setAttribute("ppos", new THREE.BufferAttribute(data.ppos, 3));
    geo.setAttribute("riverFlow", new THREE.BufferAttribute(data.riverFlow, 3));
    geo.setAttribute("aRiverS", new THREE.BufferAttribute(data.riverS, 1));
    // Pack shelter beside the existing road channel; no extra attribute slot.
    const groundSupport=new Float32Array(data.roadSd.length*2);
    for(let i=0;i<data.roadSd.length;i++){groundSupport[i*2]=data.roadSd[i];groundSupport[i*2+1]=data.snowRetention?.[i]??-1;}
    geo.setAttribute("aRoadSd", new THREE.BufferAttribute(groundSupport, 2));
    geo.setAttribute("aVolcanism", new THREE.BufferAttribute(data.volcanism ?? new Float32Array(data.slopes.length), 1));
    geo.setAttribute("aDuneSand", new THREE.BufferAttribute(data.duneSand ?? new Float32Array(data.slopes.length), 1));
    const wetland = hasLivingGround(this.def) && !!data.estuaryShelter?.some(s => s > .12);
    if (wetland) geo.setAttribute("aEstuaryShelter", new THREE.BufferAttribute(data.estuaryShelter!, 1));
    const bank=!!data.riverBankSurface,inland=!!data.wetlandSurface;
    // Three also declares its built-in UV attribute. Pack both local habitats
    // when present together to keep the maximal terrain variant within 16 slots.
    if(bank&&inland){
      const packed=new Float32Array(data.slopes.length*4);
      for(let i=0;i<data.slopes.length;i++){packed[i*4]=data.wetlandSurface![i*2];packed[i*4+1]=data.wetlandSurface![i*2+1];packed[i*4+2]=data.riverBankSurface![i*2];packed[i*4+3]=data.riverBankSurface![i*2+1];}
      geo.setAttribute("aWetlandBankSurface",new THREE.BufferAttribute(packed,4));
    }else{
      if(bank)geo.setAttribute("aRiverBankSurface",new THREE.BufferAttribute(data.riverBankSurface!,2));
      if(inland)geo.setAttribute("aWetlandSurface",new THREE.BufferAttribute(data.wetlandSurface!,2));
    }
    const butte=!!data.butteRock;
    if(butte)geo.setAttribute('aButteRock',new THREE.BufferAttribute(data.butteRock!,1));
    const canyon=!!data.canyonSurface;
    if(canyon)geo.setAttribute('aCanyonSurface',new THREE.BufferAttribute(data.canyonSurface!,2));
    geo.setAttribute("aGlacierIce", new THREE.BufferAttribute(data.glacierIce ?? new Float32Array(data.slopes.length*2), 2));
    geo.setIndex(data.indices ? new THREE.BufferAttribute(data.indices,1) : sharedIndex);
    // The worker MEASURES this sphere off the emitted vertices. It used to be
    // estimated from the node's horizontal span (size·R·1.6), which a cliff blows
    // straight through — near-vertical ground puts vertices multiples of the span
    // above/below the patch center, outside the sphere, so three.js frustum-culled
    // cliff patches that were still on screen (they popped out at the viewport edge).
    geo.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(data.boundCenter[0], data.boundCenter[1], data.boundCenter[2]),
      data.boundRadius,
    );
    // Optional habitats compile only on affected tiles. All pooled variants
    // share this planet's existing water, weather, cloud and phase uniforms.
    let material:THREE.Material;
    if(bank){
      const variants=this.riverBankMaterials??=new Map<number,THREE.Material>(),key=(wetland?1:0)+(butte?2:0)+(canyon?4:0)+(inland?8:0);
      material=variants.get(key)??makeTerrainMaterial(this.def,this.sampler.amplitude,wetland,this.material,butte,canyon,inland,true);
      variants.set(key,material);
    }else if(inland){
      const variants=this.inlandMaterials??=new Map<number,THREE.Material>(),key=(wetland?1:0)+(butte?2:0)+(canyon?4:0);
      material=variants.get(key)??makeTerrainMaterial(this.def,this.sampler.amplitude,wetland,this.material,butte,canyon,true);
      variants.set(key,material);
    }else if(canyon){
      const variants=this.canyonMaterials??=new Map<number,THREE.Material>(),key=(wetland?1:0)+(butte?2:0);
      material=variants.get(key)??makeTerrainMaterial(this.def,this.sampler.amplitude,wetland,this.material,butte,true);
      variants.set(key,material);
    }else material = butte ? (wetland
      ? (this.wetlandButteMaterial??=makeTerrainMaterial(this.def,this.sampler.amplitude,true,this.material,true))
      : (this.butteMaterial??=makeTerrainMaterial(this.def,this.sampler.amplitude,false,this.material,true))) : wetland
      ? (this.wetlandMaterial ??= makeTerrainMaterial(this.def, this.sampler.amplitude, true, this.material))
      : this.material;
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(data.center[0], data.center[1], data.center[2]);
    mesh.visible = false;
    mesh.receiveShadow = true; // the ground takes cast shadows (characters/props/ship)
    // Actual canyon terrain occludes the existing player-centred sun/moon
    // maps. The immutable reach bound is conservative; unaffected terrain
    // adds no shadow pass, and ordinary visibility/frustum culling still apply.
    mesh.castShadow = (node.canyonReliefM??0)>0;
    node.mesh = mesh;
    node.state = "ready";
    this.terrain.add(mesh);
    if(data.inlandWater?.chunks.length){
      if(!this.inlandWaterGroup){this.inlandWaterGroup=new THREE.Group();this.inlandWaterGroup.name='inland-wetlands';this.group.add(this.inlandWaterGroup);}
      this.inlandWaterMat??=makeInlandWetlandMaterial(this.def,getDetailTexture());
      node.inlandWater=mountInlandWetlandWater(data.inlandWater,this.inlandWaterMat);
      node.inlandWater.visible=false;this.inlandWaterGroup.add(node.inlandWater);
    }
    if (node.level === this.scatterLevel) this.addScatter(node, "near", data.grass);
    else if (node.level === this.scatterLevel - 2) this.addScatter(node, "meadow");
    else if (node.level === this.forestLevel) this.addScatter(node, "forest", undefined, data.wetland, data.coastal, data.savanna, data.inlandWetland, data.riparian);
    else if (node.level === this.farLevel && this.farLevel !== this.forestLevel) this.addScatter(node, "far", undefined, undefined, data.coastal, data.savanna, data.inlandWetland);
  }

  private setVisible(node: QuadNode, visible: boolean): void {
    if (node.mesh) node.mesh.visible = visible;
    if(node.inlandWater)node.inlandWater.visible=visible;
    if (node.scatter) for (const s of node.scatter) updateScatterLod(s, this.scatterEye, visible);
  }

  /**
   * Walk-blocking scatter props (tree trunks, stumps, boulders): lateral
   * push-out against radial cylinders, same contract as the settlement
   * colliders — planet-local metres in, CORRECTED position in `out`, true
   * if anything pushed. The radial ground clamp after re-seats the feet.
   */
  resolveScatterCollision(x: number, y: number, z: number, radius: number, out: { x: number; y: number; z: number }): boolean {
    if (this.scatterCells.size === 0 && this.standCells.size === 0) return false;
    let px = x, py = y, pz = z;
    let hit = false;
    const cx = Math.floor(x / SCATTER_CELL), cy = Math.floor(y / SCATTER_CELL), cz = Math.floor(z / SCATTER_CELL);
    if (this.scatterQueryCell?.[0] !== cx || this.scatterQueryCell[1] !== cy || this.scatterQueryCell[2] !== cz) {
      this.scatterQueryCell = [cx, cy, cz];
      this.scatterQueryKeys = [];
      for (let ix = cx - 1; ix <= cx + 1; ix++)
        for (let iy = cy - 1; iy <= cy + 1; iy++)
          for (let iz = cz - 1; iz <= cz + 1; iz++) this.scatterQueryKeys.push(`${ix},${iy},${iz}`);
    }
    for (const key of this.scatterQueryKeys) {
          const bucket = this.scatterCells.get(key);
          if (bucket) for (const e of bucket) {
            // Cylinder axis = outward radial at the prop's root.
            const al = Math.hypot(e.x, e.y, e.z);
            if (al < 1e-6) continue;
            const ax = e.x / al, ay = e.y / al, az = e.z / al;
            const dx = px - e.x, dy = py - e.y, dz = pz - e.z;
            const along = dx * ax + dy * ay + dz * az;
            if (along < (e.minAlong ?? -2) || along > e.h) continue; // above the prop (or under a ledge)
            const lx = dx - ax * along, ly = dy - ay * along, lz = dz - az * along;
            const d2 = lx * lx + ly * ly + lz * lz;
            const min = e.r + radius;
            if (d2 >= min * min) continue;
            if (d2 < 1e-8) {
              // A fast body's center can land exactly on the trunk axis.
              // Choose a deterministic tangent instead of leaving it embedded.
              let tx = -ay, ty = ax, tz = 0;
              let tl = Math.hypot(tx, ty, tz);
              if (tl < 1e-6) { tx = 0; ty = -az; tz = ay; tl = Math.hypot(tx, ty, tz); }
              px += tx / tl * min;
              py += ty / tl * min;
              pz += tz / tl * min;
            } else {
              const d = Math.sqrt(d2);
              const k = (min - d) / d;
              px += lx * k;
              py += ly * k;
              pz += lz * k;
            }
            hit = true;
          }
          // Boulder FLANKS: walking in from downhill (feet below the dome
          // surface) meets the rock's cross-section as a wall — the stand path
          // only owns contact AT/ABOVE the surface, which let players clip in
          // from underneath on steep hillsides.
          //
          // ⚠⚠ THE `domeAt` HERE MUST BE THE SAME PROFILE THE STAND USES. This
          // gate ("am I on the surface, or under it?") and the ground clamp's
          // catch window are the two halves of one decision, and when they
          // disagreed the character oscillated: the clamp lifted the feet onto
          // the dome, the next frame's flank test judged those same feet to be
          // under it and shoved them back out, and the frame after lifted them
          // again. Measured on a 5 m boulder: 31 push-backs and 16 vertical
          // reversals over a single approach, and the player never got above
          // 0.48 m of a 5 m rock. That was the "rocks jiggle" report.
          const stands = this.standCells.get(key);
          if (stands) for (const e of stands) {
            const al = Math.hypot(e.x, e.y, e.z);
            if (al < 1e-6 || e.top < 1e-3) continue;
            const ax = e.x / al, ay = e.y / al, az = e.z / al;
            const dx = px - e.x, dy = py - e.y, dz = pz - e.z;
            const along = dx * ax + dy * ay + dz * az; // height above the rock root
            if (along < -3 || along > e.top + 0.2) continue;
            const lx = dx - ax * along, ly = dy - ay * along, lz = dz - az * along;
            const lat2 = lx * lx + ly * ly + lz * lz;
            const reach = e.a + radius;
            if (lat2 >= reach * reach) continue;
            const domeAt = e.top * standDomeProfile(lat2 / (e.a * e.a));
            if (along >= domeAt - 0.5) continue; // on/near the surface: stand owns it
            // Push out to the ELLIPSOID cross-section at the feet's height —
            // deliberately the wider of the two curves, so a wall never leaves
            // the body inside the visible mesh.
            const hN = Math.max(0, along) / e.top;
            const cross = e.a * Math.sqrt(Math.max(0, 1 - hN * hN));
            const minL = cross + radius * 0.9;
            if (lat2 >= minL * minL) continue;
            if (lat2 < 1e-8) {
              // Dead-centre on the rock's axis (a fast body can land exactly
              // there): pick a deterministic tangent, same as the trunk branch
              // above, rather than leaving the body embedded with no push.
              let tx = -ay, ty = ax, tz = 0;
              let tl = Math.hypot(tx, ty, tz);
              if (tl < 1e-6) { tx = 0; ty = -az; tz = ay; tl = Math.hypot(tx, ty, tz); }
              px += tx / tl * minL;
              py += ty / tl * minL;
              pz += tz / tl * minL;
            } else {
              const lat = Math.sqrt(lat2);
              const k = (minL - lat) / lat;
              px += lx * k;
              py += ly * k;
              pz += lz * k;
            }
            hit = true;
          }
        }
    if (hit) {
      out.x = px;
      out.y = py;
      out.z = pz;
    }
    return hit;
  }

  /**
   * Bolt sweep against the SCATTER PROPS (2026-08-14, user call: "geometry in the
   * world STOPS bolts") — the same trunk cylinders + boulder domes the character's
   * wall solve collides with, so what blocks your feet blocks your fire. Segment in
   * planet-LOCAL metres; returns the earliest parametric t in [0,1], or −1 when
   * clear. Same contract as settlementColliders.segmentHit / homesteadSegmentHit.
   * Trunk-only for trees (canopies never block — matching the walk rule).
   */
  scatterSegmentHit(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
    if (this.scatterCells.size === 0 && this.standCells.size === 0) return -1;
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const segLen = Math.hypot(dx, dy, dz);
    if (segLen < 1e-6) return -1;
    let best = -1;
    // Visit every hash cell the segment's AABB touches, ±1 (SCATTER_CELL exceeds
    // any collider's reach, so one ring of neighbors covers props leaning in).
    const cx0 = Math.floor(Math.min(ax, bx) / SCATTER_CELL) - 1, cx1 = Math.floor(Math.max(ax, bx) / SCATTER_CELL) + 1;
    const cy0 = Math.floor(Math.min(ay, by) / SCATTER_CELL) - 1, cy1 = Math.floor(Math.max(ay, by) / SCATTER_CELL) + 1;
    const cz0 = Math.floor(Math.min(az, bz) / SCATTER_CELL) - 1, cz1 = Math.floor(Math.max(az, bz) / SCATTER_CELL) + 1;
    for (let ix = cx0; ix <= cx1; ix++)
      for (let iy = cy0; iy <= cy1; iy++)
        for (let iz = cz0; iz <= cz1; iz++) {
          const key = `${ix},${iy},${iz}`;
          const bucket = this.scatterCells.get(key);
          if (bucket) for (const e of bucket) {
            // Finite radial cylinder (root e.xyz, axis = outward radial, radius
            // e.r, height e.h) vs the segment: quadratic in the axis-perp plane.
            const al = Math.hypot(e.x, e.y, e.z);
            if (al < 1e-6) continue;
            const ux = e.x / al, uy = e.y / al, uz = e.z / al;
            const wx = ax - e.x, wy = ay - e.y, wz = az - e.z;
            const dU = dx * ux + dy * uy + dz * uz;
            const wU = wx * ux + wy * uy + wz * uz;
            const px = dx - ux * dU, py = dy - uy * dU, pz = dz - uz * dU;
            const qx = wx - ux * wU, qy = wy - uy * wU, qz = wz - uz * wU;
            const a2 = px * px + py * py + pz * pz;
            const b2 = qx * px + qy * py + qz * pz;
            const c2 = qx * qx + qy * qy + qz * qz - e.r * e.r;
            // Intersect the radial interval with the axial slab. Testing only
            // the first side hit misses shots entering a short bent-trunk
            // section through a cap, including shots parallel to its axis.
            let enter = 0, exit = 1;
            if (a2 < 1e-12) {
              if (c2 > 0) continue;
            } else {
              const disc = b2 * b2 - a2 * c2;
              const roundoff = Number.EPSILON * 16 * (b2 * b2 + Math.abs(a2 * c2) + 1);
              if (disc < -roundoff) continue;
              const root = Math.sqrt(Math.max(0, disc));
              enter = Math.max(enter, (-b2 - root) / a2);
              exit = Math.min(exit, (-b2 + root) / a2);
            }
            const bottom = e.minAlong ?? -0.5;
            if (Math.abs(dU) < 1e-12) {
              if (wU < bottom || wU > e.h) continue;
            } else {
              const capA = (bottom - wU) / dU, capB = (e.h - wU) / dU;
              enter = Math.max(enter, Math.min(capA, capB));
              exit = Math.min(exit, Math.max(capA, capB));
            }
            if (enter <= exit && (best < 0 || enter < best)) best = enter;
          }
          const stands = this.standCells.get(key);
          if (stands) for (const e of stands) {
            // Boulder DOME: march the segment and test against the same smoothstep
            // height field the stand/walk solves use — one profile, one truth.
            const al = Math.hypot(e.x, e.y, e.z);
            if (al < 1e-6 || e.top < 1e-3) continue;
            // Coarse reject: segment midpoint vs the rock's bounding reach.
            const mx = ax + dx * 0.5 - e.x, my = ay + dy * 0.5 - e.y, mz = az + dz * 0.5 - e.z;
            const reach = Math.max(e.a, e.top) + segLen * 0.5 + 1;
            if (mx * mx + my * my + mz * mz > reach * reach) continue;
            const ux = e.x / al, uy = e.y / al, uz = e.z / al;
            const steps = Math.max(2, Math.ceil(segLen / 0.75));
            for (let i = 0; i <= steps; i++) {
              const t = i / steps;
              if (best >= 0 && t >= best) break;
              const sx = ax + dx * t - e.x, sy = ay + dy * t - e.y, sz = az + dz * t - e.z;
              const along = sx * ux + sy * uy + sz * uz;
              if (along < -0.5 || along > e.top) continue;
              const lx = sx - ux * along, ly = sy - uy * along, lz = sz - uz * along;
              const lat2 = lx * lx + ly * ly + lz * lz;
              if (lat2 >= e.a * e.a) continue;
              if (along < e.top * standDomeProfile(lat2 / (e.a * e.a))) { best = t; break; }
            }
          }
        }
    return best;
  }

  /**
   * SETTLEMENT STREET TREES (2026-07-31, user ask: "trees, same as from nearby
   * biomes, in settlements"): build instanced tree meshes at PLANET-LOCAL base
   * positions (worldgen buildSettlementTrees sites — off-road, off-parcel by
   * construction). Species come from the settlement's OWN biome via the same
   * flora library the forests draw from, so town trees match the countryside.
   * Geometry is LIBRARY-SHARED: meshes are tagged sharedGeometry so region
   * teardown disposes only the InstancedMesh, never the library geometry.
   */
  buildSettlementTreeMeshes(sites: Float64Array): THREE.InstancedMesh[] {
    const STRIDE = 6; // == SETTLEMENT_TREE_STRIDE
    const n = Math.floor(sites.length / STRIDE);
    if (n === 0 || !this.def.hasFlora) return [];
    if (this.def.archetype !== "terran" && this.def.archetype !== "ocean") return [];
    // Settlement-local biome decides the mix; a tiny seeded LCG covers the
    // library's rng-taking helpers deterministically.
    let s = (Math.imul(this.def.seed.length, 2654435761) ^ 0x51e3) >>> 0;
    for (let i = 0; i < this.def.seed.length; i++) s = (Math.imul(s ^ this.def.seed.charCodeAt(i), 1664525) + 1013904223) >>> 0;
    const rng = (): number => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 0x100000000);
    const ts: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
    const d0 = new THREE.Vector3(sites[0], sites[1], sites[2]).normalize();
    this.sampler.sample(d0.x, d0.y, d0.z, ts, true);
    const season = seasonAt(ts.temp, rng, ts.h * this.sampler.amplitude, this.def.climate.snowline * this.sampler.amplitude);
    const m = maturityAt(d0.x, d0.y, d0.z, this.maturitySeed);
    const bucket = maturityBucket(m);
    const mix = forestMix(ts.temp, ts.moist, rng);
    const prim = this.floraLib.variants(mix.primary, season, bucket);
    const sec = this.floraLib.variants(mix.secondary, season, bucket);
    // Bucket sites per variant geometry (one InstancedMesh each).
    const byGeo = new Map<THREE.BufferGeometry, number[]>();
    for (let i = 0; i < n; i++) {
      const roll = sites[i * STRIDE + 3];
      const geo = roll < 0.72
        ? prim[Math.min(prim.length - 1, Math.floor((roll / 0.72) * prim.length))]
        : sec[Math.min(sec.length - 1, Math.floor(((roll - 0.72) / 0.28) * sec.length))];
      if (!geo) continue;
      const list = byGeo.get(geo) ?? [];
      list.push(i);
      byGeo.set(geo, list);
    }
    const out: THREE.InstancedMesh[] = [];
    const up = new THREE.Vector3(), pos = new THREE.Vector3();
    const qa = new THREE.Quaternion(), qy = new THREE.Quaternion();
    const scale = new THREE.Vector3(), mtx = new THREE.Matrix4();
    const Y = new THREE.Vector3(0, 1, 0);
    for (const [geo, list] of byGeo) {
      const mats = makeFloraMaterials({ birth: floraTime(), fadeFar: 0, fadeNear: 0, height: floraHeight(geo) });
      const inst = new THREE.InstancedMesh(geo, mats.material, list.length);
      inst.customDepthMaterial = mats.depthMaterial;
      list.forEach((i, k) => {
        const o = i * STRIDE;
        pos.set(sites[o], sites[o + 1], sites[o + 2]);
        up.copy(pos).normalize();
        // Live trees stand VERTICAL (radial up), yaw for variety.
        qa.setFromUnitVectors(Y, up);
        qy.setFromAxisAngle(Y, sites[o + 5]);
        qa.multiply(qy);
        const sk = sites[o + 4] * (1.1 + 0.55 * m);
        scale.set(sk, sk, sk);
        mtx.compose(pos, qa, scale);
        inst.setMatrixAt(k, mtx);
      });
      inst.instanceMatrix.needsUpdate = true;
      geo.computeBoundingSphere();
      inst.castShadow = true;
      inst.receiveShadow = true;
      inst.userData.sharedGeometry = true; // library-owned — never dispose
      out.push(inst);
    }
    return out;
  }

  /**
   * Rockiness is a BIOME trait: boulders define mountains, dry badlands and
   * the harsh archetypes; living lowlands retain scattered rooted stone groups.
   * 0 = no rocks to speak of, 1 = strewn with them.
   */
  private rockiness(moist: number, mountain: number): number {
    const base = this.def.archetype === "terran" || this.def.archetype === "ocean" ? 0.22 : 0.5;
    const sm = (x: number): number => Math.min(1, Math.max(0, x));
    return Math.min(1, base + sm(mountain * 1.3 - 0.1) + 0.6 * sm((-moist - 0.1) / 0.45));
  }

  /**
   * Highest climbable scatter dome (boulders) under a planet-LOCAL point, as a
   * LOCAL radius (== world radius; rotation preserves length), or null when
   * standing over none. The character's ground clamp
   * takes the max of terrain and this, so rocks are walked UP, not pushed off.
   */
  scatterStandRadius(x: number, y: number, z: number): number | null {
    if (this.standCells.size === 0) return null;
    let best: number | null = null;
    const cx = Math.floor(x / SCATTER_CELL), cy = Math.floor(y / SCATTER_CELL), cz = Math.floor(z / SCATTER_CELL);
    for (let ix = cx - 1; ix <= cx + 1; ix++)
      for (let iy = cy - 1; iy <= cy + 1; iy++)
        for (let iz = cz - 1; iz <= cz + 1; iz++) {
          const bucket = this.standCells.get(`${ix},${iy},${iz}`);
          if (!bucket) continue;
          for (const e of bucket) {
            const al = Math.hypot(e.x, e.y, e.z);
            if (al < 1e-6) continue;
            const ax = e.x / al, ay = e.y / al, az = e.z / al;
            const dx = x - e.x, dy = y - e.y, dz = z - e.z;
            const along = dx * ax + dy * ay + dz * az;
            const lx = dx - ax * along, ly = dy - ay * along, lz = dz - az * along;
            const lat2 = lx * lx + ly * ly + lz * lz;
            if (lat2 >= e.a * e.a) continue;
            // Smooth dome height at this lateral offset — see standDomeProfile
            // for why this is NOT the ellipsoid it looks like it should be.
            const dome = e.top * standDomeProfile(lat2 / (e.a * e.a));
            const r = al + dome;
            if (best === null || r > best) best = r;
          }
        }
    return best;
  }

  private destroyNode(node: QuadNode): void {
    node.buildAbort?.abort(); node.buildAbort = null;
    if(node.inlandWater){disposeInlandWetlandWater(node.inlandWater);node.inlandWater=null;}
    this.floorNodes.delete(node);
    this.floorIndex.delete(node);
    if (node.children) {
      for (const c of node.children) this.destroyNode(c);
      node.children = null;
    }
    if (node.mesh) {
      this.terrain.remove(node.mesh);
      node.mesh.geometry.dispose();
      node.mesh = null;
    }
    if (node.scatter) {
      for (const s of node.scatter) {
        this.shoreRocks.delete(s);
        this.riverRocks.delete(s);this.riverRockFoam?.delete(s);
        s.removeFromParent();
        disposeFloraMeshMaterials(s);
        // Grass tiles are plain Meshes (flora/grassBlades.ts), not InstancedMesh:
        // they have no dispose(), and their geometry is per-tile and large, so it
        // is the thing that has to go. Prop tiles are the opposite — shared
        // template geometry that must be left alone.
        if (s.userData.ownGeometry) s.geometry.dispose();
        else s.dispose?.();
      }
      node.scatter = null;
    }
    node.renderCell?.dispose(); node.renderCell = null;
    const stands = this.standRefs.get(node);
    if (stands) {
      for (const { key, e } of stands) {
        const bucket = this.standCells.get(key);
        if (bucket) {
          const i = bucket.indexOf(e);
          if (i >= 0) bucket.splice(i, 1);
          if (bucket.length === 0) this.standCells.delete(key);
        }
      }
      this.standRefs.delete(node);
    }
    const refs = this.scatterRefs.get(node);
    if (refs) {
      for (const { key, e } of refs) {
        const bucket = this.scatterCells.get(key);
        if (bucket) {
          const i = bucket.indexOf(e);
          if (i >= 0) bucket.splice(i, 1);
          if (bucket.length === 0) this.scatterCells.delete(key);
        }
      }
      this.scatterRefs.delete(node);
    }
    node.state = "empty";
  }

  /** Forest and near patches can finish in either order. Reconcile only the
   * floor batches when their local anchors change; never reseed trees/rocks.
   * Four builds per update bound streaming work while the index is filling. */
  private refreshForestFloor(): void {
    let built = 0;
    for (const node of this.floorNodes) {
      if (node.floorRevision === this.floorIndex.revision || !node.scatter) continue;
      if (built >= 4) break;
      const anchors = this.floorIndex.query(node.center, this.def.radius * node.size * .8 + 18);
      const signature = anchors.map(a => `${a.id}:${a.x},${a.y},${a.z},${a.radius}`).join('|');
      node.floorRevision = this.floorIndex.revision;
      if (signature === node.floorSignature) continue;
      node.floorSignature = signature;
      for (const mesh of node.scatter.filter(s => s.userData.forestFloor)) {
        mesh.removeFromParent();
        disposeFloraMeshMaterials(mesh);mesh.dispose();
      }
      node.scatter = node.scatter.filter(s => !s.userData.forestFloor);
      const floor = buildForestFloor(this.def,this.sampler,this.clutter,node,anchors,this.clearingFreq,this.maturitySeed);
      for (const mesh of floor) {
        this.addScatterMesh(node,mesh);mesh.visible=false;node.scatter.push(mesh);
      }
      built++;
    }
  }

  private addScatterMesh(node: QuadNode, mesh: THREE.Mesh): void {
    if (!node.renderCell) { node.renderCell = new RenderCell(); this.terrain.add(node.renderCell); }
    node.renderCell.add(mesh);
  }

  /**
   * THE SCATTER CHOKE POINT — every band (near / forest / far) routes here, so this is
   * the one place decoration can be turned off for a whole planet.
   *
   * ⚠ A CACHE PAD DECORATES NOTHING. The pad is a real planet by design (see
   * `isCachePadDef`), so the far-band boulder pass — which runs for EVERY archetype,
   * deliberately, because rocky ground should read from kilometres out — was seeding
   * boulders at the pad datum, i.e. straight up through the sunken floors of a dungeon
   * built on that same datum. Live play: "planet rocks render inside cache tunnels".
   * Gating each individual pass on the pad would be the same omission waiting to
   * happen the next time a pass is added, so the bail is here, at the top, once.
   */
  private addScatter(node: QuadNode, band: "near" | "forest" | "far" | "meadow", workerGrass?: GrassPatchData, workerWetland?: WetlandData, workerCoastal?: CoastalData, workerSavanna?: SavannaData, workerInland?: InlandWetlandData, workerRiparian?: RiparianData): void {
    if (isCachePadDef(this.def)) return;
    if (band === 'meadow') {
      node.scatter=buildMeadowCover(this.def,this.sampler,this.clutter,node,this.clearingFreq,this.maturitySeed);
      for(const mesh of node.scatter)this.addScatterMesh(node,mesh);
      return;
    }
    let rng = rngFromString(`${this.def.seed}:scatter:${node.face}:${node.level}:${node.u0}:${node.v0}`);
    const dir: number[] = [0, 0, 0];
    const up = new THREE.Vector3();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const m = new THREE.Matrix4();
    const Y = new THREE.Vector3(0, 1, 0);
    const X_AXIS = new THREE.Vector3(1, 0, 0);
    const meshes: THREE.InstancedMesh[] = [];
    const support = new ScatterSupport(this.sampler, this.def);

    const ts: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
    const spinQ = new THREE.Quaternion();
    const tiltE = new THREE.Euler();
    const tint = new THREE.Color();
    // Scratch for the ground-normal solve (stumps/logs/grass hug the slope).
    const gT1 = new THREE.Vector3();
    const gT2 = new THREE.Vector3();
    const gA = new THREE.Vector3();
    const gB = new THREE.Vector3();
    const gN = new THREE.Vector3();
    const gAxis = new THREE.Vector3();
    const nodeColliders: { key: string; e: ScatterCollider }[] = [];
    const nodeStands: { key: string; e: ScatterStand }[] = [];
    const floorAnchors: FloorAnchor[] = [];
    let floorOrdinal = 0;
    const floorOwner = `${node.face}:${node.level}:${node.u0}:${node.v0}`;
    const place = (
      geo: THREE.BufferGeometry,
      count: number,
      sMin: number,
      sMax: number,
      flora: boolean,
      jitter = false,
      opts?: { coarse?: THREE.BufferGeometry; clearing?: boolean; habitatTree?: boolean; understory?: boolean; groundAlign?: boolean; patch?: boolean; collide?: { r: number; h: number }; stand?: boolean; standAlong?: number; fadeFar?: number; fadeNear?: number },
    ) => {
      if (count <= 0) return;
      if (!geo.boundingBox) geo.computeBoundingBox();
      const bounds = geo.boundingBox!;
      const halfExtent = Math.max(bounds.max.x, -bounds.min.x, bounds.max.z, -bounds.min.z);
      // Spatial cells share a material pair with the same birth (dissolve-in on build), fadeFar (soft distance
      // edge) and fadeNear (far-band crossfade). The depth twin makes the cast
      // shadow sway and dissolve in step with the mesh.
      // height: the wind bend is a fraction of the plant's OWN height, so grass
      // folds over while a tree on the same shader only leans (see FloraFade).
      // rigid: `flora` IS the is-this-a-plant flag at every call site (it also
      // picks the flora sampler fields, the snowline cut and the waterline
      // margin), so the rocks — the only things placed with flora=false — opt
      // out of the wind entirely. Deriving it here rather than from a separate
      // option means a new boulder placement cannot forget to.
      const rigid = !flora;
      const fade = {
        birth: floraTime(),
        fadeFar: opts?.fadeFar ?? 0,
        fadeNear: opts?.fadeNear ?? 0,
        height: floraHeight(geo),
        rigid,
        simpleWind: band === "far",
      };
      const inst = makePooledFloraMesh(geo, count, fade);
      // Tagged for probes: which fade band this mesh belongs to (_florashadow.mjs
      // checks the far band is absent from the shadow map when you stand in it).
      inst.userData.fadeNear = opts?.fadeNear ?? 0;
      inst.userData.fadeFar = opts?.fadeFar ?? 0;
      inst.userData.rigid = rigid;
      let placed = 0;
      let accepted = 0;
      // Spatial cell of each placed instance (see the split below).
      const cells = scatterCells(this.def.radius, node.size, count);
      const cellOf = new Uint8Array(count);
      for (let i = 0; i < count * 2 && accepted < count; i++) {
        const u = node.u0 + rng() * node.size;
        const v = node.v0 + rng() * node.size;
        cellOf[placed] = Math.min(cells - 1, Math.floor((u - node.u0) / node.size * cells))
          + cells * Math.min(cells - 1, Math.floor((v - node.v0) / node.size * cells));
        faceDir(node.face, u, v, dir);
        up.set(dir[0], dir[1], dir[2]);
        this.sampler.sample(up.x, up.y, up.z, ts, flora);
        const canyonSuppression = flora && (opts?.collide || opts?.habitatTree) ? canyonCanopySuppression(ts) : 0;
        const coastalWeight = opts?.habitatTree ? coastalForestWeight(this.def,this.sampler,up.x,up.y,up.z,ts) : 0;
        const savannaWeight = opts?.habitatTree ? savannaForestWeight(this.def,this.sampler,up.x,up.y,up.z,ts) : 0;
        const duneSuppression = groundDuneSuppression(ts.duneSand ?? 0, !flora ? 'rock' : opts?.habitatTree ? 'tree' : 'plant');
        const r = this.def.radius + ts.h * this.sampler.amplitude;
        // Nothing grows (or stands) in carved water or below the sea. The wet
        // mask only spans the channel core — the river's actual waterline can
        // reach the carve edge, so also refuse anywhere the river SURFACE
        // stands above this ground (trees were wading in the margins).
        if (ts.river > 0.3) continue;
        if ((ts.glacierIceM ?? 0) > 1) continue;
        if ((ts.lavaDepthM ?? 0) > 0) continue;
        if ((ts.riverWs ?? -1e9) > ts.h - 0.4 / this.sampler.amplitude) continue;
        if (this.def.seaRadius !== null && r < this.def.seaRadius + (flora ? 2 : 0.5)) continue;
        const coldTree=opts?.habitatTree&&alpineEnabled(this.def)?alpineTree(ts.temp,ts.h*this.sampler.amplitude,this.def.climate.snowline*this.sampler.amplitude,ts.snowRetention??-1):1;
        if (flora && ts.temp < (opts?.habitatTree&&alpineEnabled(this.def)?-.74:-.55)) continue; // above the snowline: bare
        if (ts.town > 0.02) continue; // settled ground — the town owns its pad
        if (opts?.understory && (ts.road < 1.8 || ts.temp < -.25 || ts.moist < .1)) continue;
        // Glades: the clearing field punches open patches into dense cover
        // (ragged edges via the small random threshold wobble).
        if (opts?.clearing && clearingAt(up.x, up.y, up.z, this.clearingFreq, this.maturitySeed ^ 0x3517) > 0.63 + rng() * 0.06) continue;
        // Meadow patches: grass/flowers grow as CONTINUOUS swaths — dense
        // inside a patch, bare between — instead of lone scattered tufts.
        if (opts?.patch && (groundPatchAt(up.x * r, up.y * r, up.z * r) < .55
          || groundSnow(ts.temp, ts.h * this.sampler.amplitude, this.def.climate.snowline * this.sampler.amplitude) > .15
          || groundBeach(r - (this.def.seaRadius ?? -1e9)) > .15)) continue;
        pos.copy(up).multiplyScalar(r);
        if (opts?.groundAlign) {
          // Ground-hugging props (stumps, logs, grass) take the terrain NORMAL:
          // finite-difference two nearby surface points and cross the offsets.
          const e = 1.5 / this.def.radius; // ~1.5 m angular step
          gT1.crossVectors(up, Math.abs(up.y) < 0.9 ? Y : X_AXIS).normalize();
          gT2.crossVectors(up, gT1);
          gA.copy(up).addScaledVector(gT1, e).normalize();
          this.sampler.sample(gA.x, gA.y, gA.z, ts, false);
          gA.multiplyScalar(this.def.radius + ts.h * this.sampler.amplitude).sub(pos);
          gB.copy(up).addScaledVector(gT2, e).normalize();
          this.sampler.sample(gB.x, gB.y, gB.z, ts, false);
          gB.multiplyScalar(this.def.radius + ts.h * this.sampler.amplitude).sub(pos);
          gN.crossVectors(gA, gB).normalize();
          if (gN.dot(up) < 0) gN.negate();
          quat.setFromUnitVectors(Y, gN);
        } else {
          // Up-aligned (trees grow toward the sky, not off the slope).
          quat.setFromUnitVectors(Y, up);
        }
        // Randomly spun, slightly tilted — no two stand alike.
        spinQ.setFromEuler(tiltE.set(range(rng, -0.07, 0.07), rng() * Math.PI * 2, range(rng, -0.07, 0.07)));
        quat.multiply(spinQ);
        const s = range(rng, sMin, sMax)*(.48+.52*coldTree);
        const sy = s * range(rng, 0.85, 1.2);
        scale.set(s, sy, s);
        // Canopies may overhang a bank; roots need actual support. Boulders
        // need their broad bases seated, so probe most of their footprint.
        const footprint = Math.max(.3, s * (rigid || opts?.groundAlign
          ? halfExtent * .75
          : (opts?.collide?.r ?? Math.min(.6, halfExtent * .12)) * 1.5));
        const relief = rigid ? Math.max(.35, (bounds.max.y - bounds.min.y) * sy * .6)
          : opts?.groundAlign ? Infinity : Math.max(.75, footprint * 1.8);
        const seat = support.radius(up, r, footprint, relief, rigid);
        if (seat === null) continue;
        // Consume the original candidate's full random stream before replacing
        // it. Coastal colonies must not reshuffle inland trees, rocks or props,
        // nor refill every removed canopy with another ordinary tree.
        if (jitter) tint.setRGB(range(rng, 0.84, 1.1), range(rng, 0.88, 1.06), range(rng, 0.84, 1.08));
        accepted++;
        if(coldTree<1&&rngFromString(`${this.def.seed}:alpine-canopy:${up.x}:${up.y}:${up.z}`)()>coldTree)continue;
        const anchorOrdinal = band !== 'far' && hasLivingGround(this.def) && (opts?.collide || !flora && opts?.stand)
          ? floorOrdinal++ : -1;
        if ((ts.wetlandWaterM??-1e9)>ts.h*this.sampler.amplitude-(flora?.18:0)) continue;
        if (opts?.habitatTree && (ts.wetlandMask??0)>0 && rngFromString(`${this.def.seed}:inland-canopy:${up.x}:${up.y}:${up.z}`)()<inlandWetlandForestWeight(ts)) continue;
        if (coastalWeight > 0 && rngFromString(`${this.def.seed}:coastal-canopy:${up.x}:${up.y}:${up.z}`)() < coastalWeight) continue;
        if (savannaWeight > 0 && rngFromString(`${this.def.seed}:savanna-canopy:${up.x}:${up.y}:${up.z}`)() < savannaWeight) continue;
        if (duneSuppression > 0 && rngFromString(`${this.def.seed}:dune-scatter:${up.x}:${up.y}:${up.z}`)() < duneSuppression) continue;
        if (canyonSuppression > 0 && rngFromString(`${this.def.seed}:canyon-canopy:${up.x}:${up.y}:${up.z}`)() < canyonSuppression) continue;
        pos.copy(up).multiplyScalar(seat);
        m.compose(pos, quat, scale);
        inst.setMatrixAt(placed, m);
        if (anchorOrdinal >= 0) {
          floorAnchors.push({ id: `${floorOwner}:${anchorOrdinal}`, x:pos.x, y:pos.y, z:pos.z,
            radius: opts?.collide ? opts.collide.r*s : halfExtent*s,
            kind: opts?.collide ? 'tree' : 'rock' });
        }
        if (jitter) {
          inst.setColorAt(placed, tint);
        }
        if (opts?.collide) {
          const e: ScatterCollider = { x: pos.x, y: pos.y, z: pos.z, r: opts.collide.r * s + 0.06, h: opts.collide.h * s };
          const key = scatterKey(pos.x, pos.y, pos.z);
          const bucket = this.scatterCells.get(key);
          if (bucket) bucket.push(e);
          else this.scatterCells.set(key, [e]);
          nodeColliders.push({ key, e });
        }
        if (opts?.stand || opts?.standAlong) {
          // Dome(s) sized from the variant's REAL bounds × this instance's
          // scale, so the walkable surface follows the prop, not an idealized
          // cylinder. Long props (fallen logs) chain several domes along
          // their local +X so the ridge is walkable end to end.
          //
          // The radius is the prop's WIDEST half-extent, undiscounted. It used
          // to be shrunk to 0.92 of that, which left the mesh's longest lobe
          // sticking out past its own collision — and a rock blob is lumpy and
          // randomly spun, so on a 7 m boulder that was ~0.9 m of visible rock
          // you could walk straight into. The shrink was only tolerable because
          // the old ellipsoid dome ended in a vertical wall: a wider radius put
          // that wall further out, where it read as a step out of nowhere. The
          // smoothstep profile (see standDomeProfile) meets the ground at zero
          // height AND zero slope, so the dome can now honestly cover the mesh.
          if (!geo.boundingBox) geo.computeBoundingBox();
          const bb = geo.boundingBox!;
          const segs = opts.standAlong ?? 1;
          gAxis.set(1, 0, 0).applyQuaternion(quat);
          const aBase = segs > 1
            ? Math.max(bb.max.z, -bb.min.z) * s * 1.7
            : Math.max(bb.max.x, -bb.min.x, bb.max.z, -bb.min.z) * s;
          for (let seg = 0; seg < segs; seg++) {
            const off = segs === 1 ? 0 : (seg / (segs - 1) - 0.5) * 2 * bb.max.x * s * 0.62;
            const e: ScatterStand = { x: pos.x + gAxis.x * off, y: pos.y + gAxis.y * off, z: pos.z + gAxis.z * off, a: aBase, top: bb.max.y * sy };
            const key = scatterKey(e.x, e.y, e.z);
            const bucket = this.standCells.get(key);
            if (bucket) bucket.push(e);
            else this.standCells.set(key, [e]);
            nodeStands.push({ key, e });
          }
        }
        placed++;
      }
      if (placed === 0) {
        inst.dispose();
        return;
      }
      inst.count = placed;
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      geo.computeBoundingSphere();
      // Trees / big props cast shadows; grass tufts don't — an InstancedMesh renders ALL
      // its instances into the shadow map, so casting a whole grass tile would be costly.
      const casts = band !== "far" && (geo.boundingSphere?.radius ?? 0) > 1.2;
      const finish = (batch: THREE.InstancedMesh): void => {
        // Placements live in instanceMatrix; the cell's own local transform is
        // identity throughout its lifetime. Planet motion still updates world.
        batch.matrixAutoUpdate = false;
        batch.castShadow = casts;
        prepareScatterBounds(batch, opts?.coarse);
        batch.receiveShadow = true;
        batch.visible = false;
        this.addScatterMesh(node,batch);
        meshes.push(batch);
      };
      if (cells === 1) { finish(inst); return; }
      // SPATIAL SPLIT. A forest tile is ~500 m across and every one of its
      // trees rode in ONE InstancedMesh, so the batch was culled, LOD-switched,
      // depth-sliced and shadow-cast as a unit: a tile whose nearest corner was
      // in range submitted all of its trees at full detail, into the shadow map
      // (whose box is 400 m — most of them outside it) and, straddling the 600 m
      // depth slice, twice. Cells of ~SCATTER_CELL_M give every pass a tighter
      // sphere to test, at the price of a few more draw calls per tile. The
      // cells share the exact same fade/wind material configuration.
      const counts = new Uint16Array(cells * cells);
      for (let i = 0; i < placed; i++) counts[cellOf[i]]++;
      const subs: (THREE.InstancedMesh | null)[] = [];
      const cursor = new Uint16Array(cells * cells);
      for (let c = 0; c < cells * cells; c++) {
        if (counts[c] === 0) { subs.push(null); continue; }
        const sub = makePooledFloraMesh(geo, counts[c], fade);
        sub.userData.fadeNear = inst.userData.fadeNear;
        sub.userData.fadeFar = inst.userData.fadeFar;
        sub.userData.rigid = rigid;
        subs.push(sub);
      }
      for (let i = 0; i < placed; i++) {
        const sub = subs[cellOf[i]]!;
        const k = cursor[cellOf[i]]++;
        inst.getMatrixAt(i, m);
        sub.setMatrixAt(k, m);
        if (inst.instanceColor) { inst.getColorAt(i, tint); sub.setColorAt(k, tint); }
      }
      for (const sub of subs) {
        if (!sub) continue;
        sub.instanceMatrix.needsUpdate = true;
        if (sub.instanceColor) sub.instanceColor.needsUpdate = true;
        finish(sub);
      }
      inst.dispose();
    };

    let rockPass = 0;
    const placeRockGroup = (geo:THREE.BufferGeometry,count:number,min:number,max:number,fade:{fadeFar?:number;fadeNear?:number;shoreline?:boolean}={}) => {
      const result=buildRockScatter(this.def,this.sampler,geo,node,{band,pass:rockPass++,count,min,max,...fade});
      if(!result)return;
      this.addScatterMesh(node,result.mesh);meshes.push(result.mesh);
      if(band==='far')return;
      if(result.rocks.some(r=>r.waterM!==undefined)){
        this.riverRocks.add(result.mesh);this.riverRockFoam?.add(result.mesh);
      }
      const sea=this.def.seaRadius;
      if(sea!==null&&result.rocks.some(r=>Math.abs(Math.hypot(r.x,r.y,r.z)-sea)<r.a+1))this.shoreRocks.add(result.mesh);
      for(const rock of result.rocks) {
        // Each fragment has its own supported, climbable contact. Use the
        // full radial footprint, including diagonal corners of the new forms.
        const e:ScatterStand={x:rock.x,y:rock.y,z:rock.z,a:rock.a,top:rock.top};
        const key=scatterKey(e.x,e.y,e.z), bucket=this.standCells.get(key);
        if(bucket)bucket.push(e);else this.standCells.set(key,[e]);
        nodeStands.push({key,e});
        if(hasLivingGround(this.def))floorAnchors.push({id:`${floorOwner}:${floorOrdinal++}`,
          x:e.x,y:e.y,z:e.z,radius:e.a,kind:'rock'});
      }
    };

    // The biome at the node center decides what lives here.
    faceDir(node.face, node.u0 + node.size / 2, node.v0 + node.size / 2, dir);
    this.sampler.sample(dir[0], dir[1], dir[2], ts, true);
    const t = ts.temp;
    const nodeHeightM = ts.h * this.sampler.amplitude;
    const snowlineM = this.def.climate.snowline * this.sampler.amplitude;
    const mo = ts.moist;
    const mountain = ts.mountain; // support probes reuse ts; retain the node's geology
    const arch = this.def.archetype;
    const sm = (x: number): number => Math.min(1, Math.max(0, x));

    if (band === "far") {
      // Same capped native placements as the four forest children. Only the
      // coarse habitat silhouettes survive here; gameplay belongs to those children.
      for (const habitat of [mountCoastalHabitat(workerCoastal ?? [], this.floraLib, this.clutter, true),
        mountSavanna(workerSavanna ?? [], this.floraLib, this.clutter, true),
        mountInlandWetland(workerInland ?? [], this.floraLib, this.clutter, true)]) {
        for (const mesh of habitat.meshes) { this.addScatterMesh(node, mesh); meshes.push(mesh); }
      }
      // FAR BAND: silhouette trees on 4x-area tiles — the forest reads from
      // kilometres out and CROSSFADES to the full builds (fadeNear) as the
      // forest band streams in underneath. No colliders: the player's ground
      // never outruns the forest band, which owns collision.
      if ((arch === "terran" || arch === "ocean") && this.def.hasFlora) {
        const edge = this.def.radius * (Math.PI / 2) * (node.size / 2);
        const areaK = (edge * edge) / (570 * 570);
        const season = seasonAt(t, rng, nodeHeightM, snowlineM);
        const m = maturityAt(dir[0], dir[1], dir[2], this.maturitySeed);
        const bucket = maturityBucket(m);
        const forestK = alpineEnabled(this.def)?alpineForest(t,mo):sm((mo + 0.1) / 0.5) * sm((t + 0.5) / 0.35) * sm((0.8 - t) / 0.35);
        if (forestK > (alpineEnabled(this.def)&&t<-.28?.025:.12)) {
          const total = Math.round(Math.min(5800, (48 + forestK * 780) * areaK) * 0.5);
          const mix = forestMix(t, mo, rng);
          const prim = this.floraLib.variantsFar(mix.primary, season, bucket);
          const sec = this.floraLib.variantsFar(mix.secondary, season, bucket);
          const sk = 0.75 + 0.55 * m;
          place(prim[Math.floor(rng() * 4)], Math.round(total * 0.5), 1.45 * sk, 2.9 * sk, true, true, { clearing: true, habitatTree: true, fadeNear: 650 });
          place(prim[Math.floor(rng() * 4)], Math.round(total * 0.3), 1.45 * sk, 2.9 * sk, true, true, { clearing: true, habitatTree: true, fadeNear: 650 });
          place(sec[Math.floor(rng() * 4)], Math.round(total * 0.2), 1.25 * sk, 2.5 * sk, true, true, { clearing: true, habitatTree: true, fadeNear: 650 });
        }
      }
      // Far-band boulders too — every archetype: rocky terrain should read
      // from as far away as the trees do, crossfading exactly the same way.
      {
        const edge = this.def.radius * (Math.PI / 2) * (node.size / 2);
        const areaK = (edge * edge) / (570 * 570);
        const rocky = this.rockiness(mo, mountain);
        placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], Math.round((0.3 + rocky * 7) * Math.min(3, areaK)), 2.5, 5.0, { fadeNear: 650 });
        if (rng() < rocky * 0.5) {
          placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], 1, 4.0, 7.0, { fadeNear: 650 });
        }
      }
      node.scatter = meshes;
      if (node.mesh) for (const sct of meshes) sct.visible = node.mesh.visible;
      return;
    }

    if (band === "forest") {
      for (const habitat of [mountWetland(workerWetland ?? [], this.floraLib, this.clutter),
        mountCoastalHabitat(workerCoastal ?? [], this.floraLib, this.clutter),
        mountSavanna(workerSavanna ?? [], this.floraLib, this.clutter),
        mountInlandWetland(workerInland ?? [], this.floraLib, this.clutter)]) {
        for (const mesh of habitat.meshes) { this.addScatterMesh(node, mesh); meshes.push(mesh); }
        for (const e of habitat.colliders) {
          const key = scatterKey(e.x, e.y, e.z), bucket = this.scatterCells.get(key);
          if (bucket) bucket.push(e); else this.scatterCells.set(key, [e]);
          nodeColliders.push({ key, e });
        }
      }
      // The forest band: large tiles, real tree counts. Lush worlds follow
      // the wet/temperate sweet spot of the Whittaker table; harsh worlds
      // (desert cacti, volcanic snags) scatter their species sparsely.
      // Hold a constant trees-per-AREA density across planet sizes: the tree
      // budget was tuned for the old ~570 m forest tile, so scale it by this
      // tile's real area. Keeps the big WORLD_SCALE'd worlds genuinely lush
      // instead of the same handful of trees spread over a huge surface.
      const edge = this.def.radius * (Math.PI / 2) * (node.size / 2); // tile world edge (m)
      const areaK = (edge * edge) / (570 * 570); // 1.0 at the old reference tile
      if (arch === "terran" || arch === "ocean") {
        // Library path: season from climate, maturity from the forest-age field.
        const season = seasonAt(t, rng, nodeHeightM, snowlineM);
        const m = maturityAt(dir[0], dir[1], dir[2], this.maturitySeed);
        const bucket = maturityBucket(m);
        let total = 0;
        if (this.def.hasFlora) {
          const forestK = alpineEnabled(this.def)?alpineForest(t,mo):sm((mo + 0.1) / 0.5) * sm((t + 0.5) / 0.35) * sm((0.8 - t) / 0.35);
          // VERY dense forests: the clearing field (see place) carves the glades
          // back out, so contrast comes from packed stands against open pockets.
          if (forestK > (alpineEnabled(this.def)&&t<-.28?.025:.12)) total = Math.round(Math.min(3600, (48 + forestK * 780) * areaK));
        } else {
          total = Math.round((4 + Math.floor(rng() * 7)) * areaK);
        }
        if (total > 0) {
          const mix = forestMix(t, mo, rng);
          const prim = this.floraLib.variants(mix.primary, season, bucket);
          const sec = this.floraLib.variants(mix.secondary, season, bucket);
          const primFar = this.floraLib.variantsFar(mix.primary, season, bucket);
          const secFar = this.floraLib.variantsFar(mix.secondary, season, bucket);
          const sk = 0.75 + 0.55 * m; // whole stands run larger in old growth
          // Walk-blocking TRUNKS (canopy excluded): radius from the species
          // table at this bucket's girth, height comfortably past the crown.
          const girthK = 0.65 + 0.6 * BUCKET_MATURITY[bucket];
          const pc = { r: SPECIES[mix.primary].trunk.radius * girthK, h: SPECIES[mix.primary].trunk.height[1] * 1.3 };
          const sc2 = { r: SPECIES[mix.secondary].trunk.radius * girthK, h: SPECIES[mix.secondary].trunk.height[1] * 1.3 };
          let variant = Math.floor(rng() * 4);
          place(prim[variant], Math.round(total * 0.45), 1.45 * sk, 2.9 * sk, true, true, { clearing: true, habitatTree: true, collide: pc, coarse: primFar[variant] });
          variant = Math.floor(rng() * 4);
          place(prim[variant], Math.round(total * 0.35), 1.45 * sk, 2.9 * sk, true, true, { clearing: true, habitatTree: true, collide: pc, coarse: primFar[variant] });
          variant = Math.floor(rng() * 4);
          place(sec[variant], Math.round(total * 0.2), 1.25 * sk, 2.5 * sk, true, true, { clearing: true, habitatTree: true, collide: sc2, coarse: secFar[variant] });
        }
      } else if (this.flora.length > 0) {
        // Legacy kinds (cactus/dead/crystal…) — unchanged behavior.
        let total = 0;
        if (this.def.hasFlora) {
          const forestK = alpineEnabled(this.def)?alpineForest(t,mo):sm((mo + 0.1) / 0.5) * sm((t + 0.5) / 0.35) * sm((0.8 - t) / 0.35);
          if (forestK > (alpineEnabled(this.def)&&t<-.28?.025:.12)) total = Math.round(Math.min(1200, (16 + forestK * 170) * areaK));
        } else {
          total = Math.round((4 + Math.floor(rng() * 7)) * areaK);
        }
        if (total > 0) {
          // Primary species by temperature (cool spikes, warm billows),
          // a minority of the other — mixed stands, never plantations.
          const pi = (t < 0.15 ? 0 : 1) % this.flora.length;
          const prim = this.flora[pi];
          const sec = this.flora[(pi + 1) % this.flora.length];
          // Dead snags lie with the slope; living kinds stand toward the sky.
          place(prim.variants[Math.floor(rng() * 4)], Math.round(total * 0.45), 1.3, 2.6, true, true, { groundAlign: prim.kind === "dead", collide: LEGACY_COLLIDE[prim.kind] });
          place(prim.variants[Math.floor(rng() * 4)], Math.round(total * 0.35), 1.3, 2.6, true, true, { groundAlign: prim.kind === "dead", collide: LEGACY_COLLIDE[prim.kind] });
          place(sec.variants[Math.floor(rng() * 4)], Math.round(total * 0.2), 1.1, 2.3, true, true, { groundAlign: sec.kind === "dead", collide: LEGACY_COLLIDE[sec.kind] });
        }
      }
      // FOREST-BAND boulders: big rocks live on the forest tiles so hillsides
      // read rocky from kilometres out (small gravel stays in the near band).
      // Temperate meadows keep occasional groups; alpine slopes carry more.
      const rocky = this.rockiness(mo, mountain);
      placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], Math.round((0.5 + rocky * 11) * Math.min(4, areaK)), 2.2, 5.0);
      if (rng() < rocky * 0.6) {
        // Occasional GIANT boulder — a climbable landmark, not a pebble.
        placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], 1, 4.0, 7.0);
      }
      this.maybeSite(node, meshes);
      // Spray colonies occupy soil pockets beside the supported boulders.
      // Their full footprints stay outside the existing climbable envelopes.
      const riverCover=(workerRiparian??[]).filter(p=>!p.fallId||!nodeStands.some(({e})=>{
        const dot=e.x*p.dir[0]+e.y*p.dir[1]+e.z*p.dir[2];
        const dx=e.x-p.dir[0]*dot,dy=e.y-p.dir[1]*dot,dz=e.z-p.dir[2]*dot;
        return dx*dx+dy*dy+dz*dz<(e.a+p.footprint+.12)**2;
      }));
      for (const mesh of mountRiparian(riverCover,this.clutter).meshes) { this.addScatterMesh(node,mesh); meshes.push(mesh); }
      this.floorIndex.set(node, floorAnchors);
      node.scatter = meshes;
      if (nodeColliders.length > 0) this.scatterRefs.set(node, nodeColliders);
      if (nodeStands.length > 0) this.standRefs.set(node, nodeStands);
      if (node.mesh) for (const sct of meshes) sct.visible = node.mesh.visible;
      return;
    }

    // The near band: ground cover only (trees come from the forest band).
    if (this.def.hasFlora) {
      const forestK = alpineEnabled(this.def)?alpineForest(t,mo):sm((mo + 0.1) / 0.5) * sm((t + 0.5) / 0.35) * sm((0.8 - t) / 0.35);
      const season = seasonAt(t, rng, nodeHeightM, snowlineM);
      // Sampling and instance buffers arrive with the terrain from its worker.
      // Keep the original stream for all subsequent near props/colliders.
      if (!workerGrass) throw new Error('Near flora patch is missing worker grass');
      rng = mulberry32(workerGrass.randomState);
      for (const batch of mountGrassFields(workerGrass.fields, { birth: floraTime(), fadeFar: 150 })) {
        batch.visible = false;
        this.addScatterMesh(node, batch);
        meshes.push(batch as unknown as THREE.InstancedMesh);
      }
      if (season !== "winter" && mo > 0.05 && t > 0 && forestK < 0.7) {
        // Flowers bloom inside the same meadow swaths the grass fills.
        place(this.clutter.flower[Math.floor(rng() * 4)], 8, 0.7, 1.3, true, true, { groundAlign: true, patch: true, fadeFar: 120 });
      }
      if (season !== "winter" && mo > 0.3 && forestK > 0.35) {
        place(this.clutter.mushroom[Math.floor(rng() * 4)], 3, 0.7, 1.4, true, true, { groundAlign: true, fadeFar: 120 });
      }
      if (forestK > 0.3) {
        // Deadfall is a RARE find — one stump per ~20 tiles, one fallen trunk
        // per ~15 (the budget goes to living trees instead). Both climbable
        // like rocks — stumps a single dome, logs a chain along their length.
        if (rng() < 0.05) place(this.clutter.stump[Math.floor(rng() * 4)], 1, 1.0, 2.0, true, true, { groundAlign: true, stand: true, fadeFar: 240 });
        if (rng() < 0.07) place(this.clutter.log[Math.floor(rng() * 4)], 1, 1.0, 2.0, true, true, { groundAlign: true, standAlong: 3, fadeFar: 240 });
      }
    }
    // Rocks are CLIMBABLE domes (stand), never lateral pushes — and their
    // NUMBERS follow the biome: strewn across mountains/badlands and harsh
    // worlds, a stray one-off in lush country.
    const rocky = this.rockiness(mo, mountain);
    placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], rocky > 0.12 ? Math.round(1 + rocky * 3) : rng() < 0.3 ? 1 : 0, 1.0, 3.2, { fadeFar: 300 });
    placeRockGroup(this.rockGeos[Math.floor(rng() * this.rockGeos.length)], rocky > 0.12 ? Math.round(rocky * 2) : rng() < 0.2 ? 1 : 0, 0.7, 1.6, { fadeFar: 260 });

    // A separate seeded pass leaves all existing land groups in place. These
    // sparse, partly immersed stones use the same support and stand contacts.
    // Reject inland/deep candidates before the eight footprint probes.
    if(this.def.seaRadius!==null)placeRockGroup(this.rockGeos[Math.floor(rng()*this.rockGeos.length)],1,1.8,4.2,{fadeFar:300,shoreline:true});

    // The near layer is low undergrowth. Canopy trees of different ages remain
    // in the forest band; do not fill the floor with scaled-down tree crowns.

    node.scatter = meshes;
    if (hasLivingGround(this.def)) {
      this.floorIndex.set(node,floorAnchors);
      this.floorNodes.add(node);
      node.floorRevision = -1;
      node.floorSignature = '\u0000'; // also build moist pockets when there are no anchors
    }
    if (nodeColliders.length > 0) this.scatterRefs.set(node, nodeColliders);
    if (nodeStands.length > 0) this.standRefs.set(node, nodeStands);
    // If the node was already resolved visible this pass, sync visibility.
    if (node.mesh) for (const s of meshes) s.visible = node.mesh.visible;
  }

  /**
   * The SITE FIELD: a rare deterministic roll per forest-band tile drops a
   * ruin — a ring of broken pillars and fallen slabs draped on the terrain.
   * Pure function of (planet seed, tile), so every player finds the same
   * ruins in the same places forever: these are the landmarks phase 7's
   * ground loot will live in.
   */
  private maybeSite(node: QuadNode, meshes: THREE.InstancedMesh[]): void {
    const rng = rngFromString(
      `${this.def.seed}:site:${node.face}:${node.u0.toFixed(5)}:${node.v0.toFixed(5)}`,
    );
    if (rng() > 0.06) return;
    const dir: number[] = [0, 0, 0];
    faceDir(node.face, node.u0 + node.size / 2, node.v0 + node.size / 2, dir);
    const up = new THREE.Vector3(dir[0], dir[1], dir[2]);
    const centerR = this.sampler.surfaceRadius(up.x, up.y, up.z);
    if (this.def.seaRadius !== null && centerR < this.def.seaRadius + 6) return; // drowned
    if (settlementInfluenceAt(this.sampler, up.x, up.y, up.z, 30) !== null) return; // settled ground

    // Tangent frame around the site center.
    const t1 = new THREE.Vector3(1, 0, 0).cross(up);
    if (t1.lengthSq() < 1e-6) t1.set(0, 0, 1).cross(up);
    t1.normalize();
    const t2 = up.clone().cross(t1);

    const pillars = 5 + Math.floor(rng() * 5);
    const slabs = 1 + Math.floor(rng() * 3);
    const ringR = range(rng, 5, 11);
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const spin = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const mtx = new THREE.Matrix4();
    const Y = new THREE.Vector3(0, 1, 0);

    const drop = (geo: THREE.BufferGeometry, count: number, pillar: boolean): void => {
      const inst = new THREE.InstancedMesh(geo, this.stoneMat, count);
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + rng() * 0.5;
        const rr = pillar ? ringR : ringR * range(rng, 0, 0.7);
        pos
          .copy(up)
          .addScaledVector(t1, (Math.cos(a) * rr) / this.def.radius)
          .addScaledVector(t2, (Math.sin(a) * rr) / this.def.radius)
          .normalize();
        const gr = this.sampler.surfaceRadius(pos.x, pos.y, pos.z);
        quat.setFromUnitVectors(Y, pos);
        if (pillar && rng() < 0.25) {
          // Toppled: lying on its side, sunk a little.
          spin.setFromEuler(new THREE.Euler(Math.PI / 2 + range(rng, -0.15, 0.15), rng() * Math.PI * 2, 0, "YXZ"));
          quat.multiply(spin);
          scale.set(1, range(rng, 2, 4.5), 1);
          pos.multiplyScalar(gr + 0.35);
        } else {
          spin.setFromEuler(new THREE.Euler(range(rng, -0.05, 0.05), rng() * Math.PI * 2, range(rng, -0.05, 0.05)));
          quat.multiply(spin);
          scale.set(1, pillar ? range(rng, 1.4, 5.5) : range(rng, 0.8, 1.3), 1);
          pos.multiplyScalar(gr - 0.15);
        }
        mtx.compose(pos, quat, scale);
        inst.setMatrixAt(i, mtx);
      }
      inst.instanceMatrix.needsUpdate = true;
      inst.castShadow = true; // site stones (pillars/slabs) are notable props — always cast
      inst.receiveShadow = true;
      inst.visible = false;
      this.addScatterMesh(node,inst);
      meshes.push(inst);
    };
    drop(this.pillarGeo, pillars, true);
    drop(this.slabGeo, slabs, false);
  }

  dispose(): void {
    this.disposed = true;
    this.cliffBuild?.abort();this.cliffBuild=null;
    this.cliffMeshes?.dispose();
    this.basinSurface?.dispose();
    this.lavaFlows?.dispose();
    this.inlandWaterMat?.dispose();this.inlandWaterMat=null;
    this.inlandWaterGroup?.removeFromParent();this.inlandWaterGroup=null;
    this.volcanicEffects?.dispose();
    this.riverRockFoam?.dispose();this.riverRockFoam=null;this.riverRocks.clear();
    this.cloudDispose?.(); // frees the cloud render target (the traverse below misses it)
    this.aurora?.dispose();
    this.weatherField?.dispose();
    for (const root of this.roots) this.destroyNode(root);
    this.material.dispose();
    this.wetlandMaterial?.dispose();
    this.inlandMaterials?.forEach(material=>material.dispose());
    this.inlandMaterials?.clear();
    this.riverBankMaterials?.forEach(material=>material.dispose());
    this.riverBankMaterials?.clear();
    this.butteMaterial?.dispose();
    this.wetlandButteMaterial?.dispose();
    this.canyonMaterials?.forEach(material=>material.dispose());
    for (const set of this.flora) for (const v of set.variants) v.dispose();
    this.floraLib.dispose();
    this.scatterCells.clear();
    this.scatterRefs.clear();
    this.standCells.clear();
    this.standRefs.clear();
    for (const set of Object.values(this.clutter)) for (const g of set) g.dispose();
    for (const g of this.rockGeos) g.dispose();
    this.pillarGeo.dispose();
    this.slabGeo.dispose();
    this.stoneMat.dispose();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Points) {
        o.geometry.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
  }
}

/**
 * Planetary rings: a banded annulus aligned with the planet's equator (the
 * spin axis is its normal). The band pattern is seeded per planet and baked
 * into a 1D texture; UVs are remapped so u runs RADIALLY — bands follow
 * radius, the way rings actually shear.
 */
function makeRing(def: PlanetDef): THREE.Object3D {
  const ring = def.ring!;
  const rng = rngFromString(`${def.seed}:ringtex`);
  const W = 256;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = 1;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(W, 1);
  let a = 0.5;
  for (let x = 0; x < W; x++) {
    // A wandering alpha with occasional clean gaps — Cassini divisions.
    a += (rng() - 0.5) * 0.3;
    a = Math.max(0.05, Math.min(1, a));
    let alpha = a;
    if (rng() < 0.04) alpha = 0.02; // gap seed
    const edge = Math.min(x / 14, (W - 1 - x) / 14, 1); // feathered rims
    img.data[x * 4] = 255;
    img.data[x * 4 + 1] = 255;
    img.data[x * 4 + 2] = 255;
    img.data[x * 4 + 3] = Math.round(alpha * edge * 255);
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.ClampToEdgeWrapping;

  const inner = def.radius * ring.inner;
  const outer = def.radius * ring.outer;
  const geo = new THREE.RingGeometry(inner, outer, 160, 1);
  // Remap UVs: u = radial fraction (RingGeometry's default is planar).
  const pos = geo.getAttribute("position");
  const uv = geo.getAttribute("uv");
  for (let i = 0; i < pos.count; i++) {
    const r = Math.hypot(pos.getX(i), pos.getY(i));
    uv.setXY(i, (r - inner) / (outer - inner), 0.5);
  }
  // A few thin layers stacked along the ring normal give it real THICKNESS — so it
  // still reads EDGE-ON from the surface (a flat disc vanishes when you're near the
  // equatorial plane). Each layer runs at a fraction of the opacity so the overlap sums
  // back to ~ring.opacity face-on (the orbit view, already fine, is unchanged).
  const LAYERS = 8;
  const thickness = (outer - inner) * 0.05; // vertical extent (spread) along the spin axis
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    color: new THREE.Color(...ring.color),
    transparent: true,
    opacity: ring.opacity * (2.0 / LAYERS), // per-layer, so the overlap sums back to ~ring.opacity
    side: THREE.DoubleSide,
    depthWrite: false,
    fog: false,
  });
  const group = new THREE.Group();
  for (let l = 0; l < LAYERS; l++) {
    const layer = new THREE.Mesh(geo, mat);
    layer.position.z = (l / (LAYERS - 1) - 0.5) * thickness;
    layer.renderOrder = 3; // under the atmosphere shell, over terrain
    group.add(layer);
  }
  // The ring plane is the equator: its normal is the spin axis.
  group.quaternion.setFromUnitVectors(
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(...def.spin.axis),
  );
  return group;
}

/**
 * Per-pixel biome shading: a stock Lambert with the color DECISION moved
 * into the fragment shader. Vertices carry smooth fields (height, river,
 * temperature, moisture, slope); every threshold — waterline, beach band,
 * cliff rock, snow — is evaluated per fragment on the interpolated fields,
 * so edges resolve at pixel resolution instead of triangle resolution.
 */
// River ripple scroll speed (phase rad/s via uRippleSpeed) and a time wrap that
// lands on a WHOLE number of ripple periods — uTime % wrap is then seamless.
// Epoch-magnitude time must never reach the shader (float32 ULP ≈ 128 s there).
const RIVER_RIPPLE_SPEED = 1.5;
const RIVER_TIME_WRAP = Math.round(600 / ((Math.PI * 2) / RIVER_RIPPLE_SPEED)) * ((Math.PI * 2) / RIVER_RIPPLE_SPEED);

export function makeTerrainMaterial(def: PlanetDef, amplitude: number, estuary = false, shared?: THREE.Material, butte = false, canyon = false, inland = false, riverBank = false): THREE.Material {
  const mat = new THREE.MeshLambertMaterial();
  const pal = landscapePalette(def);
  const v3 = (c: [number, number, number]): THREE.Vector3 => new THREE.Vector3(c[0], c[1], c[2]);
  const seaRel = def.seaRadius !== null ? (def.seaRadius - def.radius) / amplitude : -1000;

  // Inland water uses a restrained mineral base beneath its reflected sky, plus
  // ripple/flow params. Kept on the material so the loop can advance the flow
  // clock + match daylight each frame (see Planet.updateWater).
  const waterU: { [k: string]: THREE.IUniform } = shared?.userData.waterUniforms ?? Object.assign(riverWaterUniforms(def.seaColor), {
    uTime: { value: 0 },
    /** Representative depth for the distant river proxy. */
    uRiverDepth: { value: 3.0 },
    uRippleFreq: { value: 0.6 }, // radians per metre along flow
    uRippleSpeed: { value: RIVER_RIPPLE_SPEED }, // downstream scroll
    uRippleAmp: { value: 0.012 }, // normal-tilt strength
  });

  // Cloud shadows (atmosphere worlds only): the Planet ctor wires uCloudCover/uCloudR to
  // the low deck once the cloud shell exists; the loop pokes uSunLocalT each frame.
  const cloudShadowU: { [k: string]: THREE.IUniform } | null = shared ? shared.userData.cloudShadowU : def.atmosphere
    ? {
        uCloudCover: { value: CLOUD_SHADOW_DUMMY },
        uCloudFrame: { value: new THREE.Matrix3() },
        uCloudR: { value: 1 },
        uSunLocalT: { value: new THREE.Vector3(0, 1, 0) },
        uCloudShadowStr: { value: 0 },
      }
    : null;

  // Distant-body PHASE shading. A moon/planet seen from afar is itself in full sunlight on
  // its day side — but the scene's ONE directional sun is dimmed for the VIEWER's local night
  // (intensity ~4% at midnight), which would black out a moon that is actually in daylight.
  // So when this body recedes (uPhaseLit ramps 0→1 with viewing distance) we shade it by ITS
  // OWN sun direction at full brightness: a crisp Lambert terminator + a faint earthshine
  // floor on the dark side → a real, mathematically-placed phase. ALWAYS present (airless
  // moons need it most). Poked each frame by Planet.updateAtmosphereCam.
  const phaseU: { [k: string]: THREE.IUniform } = shared?.userData.phaseU ?? {
    uPhaseSun: { value: new THREE.Vector3(0, 1, 0) }, // toward the star, planet-local
    uPhaseLit: { value: 0 }, // 0 = normal local lighting (on/near surface) … 1 = distant phase
    uPhaseColor: { value: new THREE.Vector3(1, 1, 1) }, // sunlit-side colour×brightness (star tint)
    uPhaseEarth: { value: PHASE_EARTHSHINE }, // earthshine floor on the unlit side
    uPhaseEclipse: { value: new THREE.Vector3(1, 1, 1) }, // lunar eclipse: white → dim copper in umbra
  };

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTable = { value: pal.table.map(v3) };
    shader.uniforms.uCliff = { value: v3(pal.cliff) };
    shader.uniforms.uSand = { value: v3(pal.sand) };
    shader.uniforms.uSnow = { value: v3(pal.snow) };
    shader.uniforms.uWater = { value: v3(pal.water) };
    shader.uniforms.uSeaRel = { value: seaRel };
    shader.uniforms.uSnowline = { value: def.climate.snowline };
    shader.uniforms.uAmplitude = { value: amplitude };
    shader.uniforms.uDetail = { value: getDetailTexture() };
    shader.uniforms.uHasFlora = { value: def.hasFlora ? 1 : 0 };
    shader.uniforms.uLivingGround = { value: hasLivingGround(def) ? 1 : 0 };
    shader.uniforms.uGroundKind = { value: new THREE.Vector3(def.archetype === 'desert' ? 1 : 0,
      def.archetype === 'barren' || def.archetype === 'volcanic' ? 1 : 0, def.archetype === 'ice' ? 1 : 0) };
    Object.assign(shader.uniforms, waterU); // by reference → updateWater drives them
    if (cloudShadowU) Object.assign(shader.uniforms, cloudShadowU); // → updateAtmosphereCam
    Object.assign(shader.uniforms, phaseU); // distant-body phase → updateAtmosphereCam

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        [
          "attribute vec4 field;",
          "attribute float slope;",
          "attribute vec3 ppos;",
          "attribute vec3 riverFlow;",
          "attribute float aRiverS;",
          "attribute vec2 aRoadSd;",
          "attribute vec2 aGlacierIce;",
          "attribute float aVolcanism;",
          "attribute float aDuneSand;",
          ...(estuary ? ["attribute float aEstuaryShelter;", "varying float vEstuaryShelter;"] : []),
          ...(butte ? ["attribute float aButteRock;", "varying float vButteRock;"] : []),
          ...(canyon ? ["attribute vec2 aCanyonSurface;", "varying vec2 vCanyonSurface;"] : []),
          ...(riverBank&&inland ? ["attribute vec4 aWetlandBankSurface;", "varying vec2 vRiverBankSurface;", "varying vec2 vWetlandSurface;"] : [
            ...(riverBank ? ["attribute vec2 aRiverBankSurface;", "varying vec2 vRiverBankSurface;"] : []),
            ...(inland ? ["attribute vec2 aWetlandSurface;", "varying vec2 vWetlandSurface;"] : []),
          ]),
          "varying vec2 vGlacierIce;",
          "varying float vVolcanism;",
          "varying float vDuneSand;",
          "varying float vRoadSd;",
          "varying float vSnowRetention;",
          "varying vec4 vField;",
          "varying float vSlope;",
          "varying vec3 vPpos;",
          "varying vec3 vNormObj;",
          "varying vec3 vWorldPos;",
          "varying vec3 vUpW;",
          "varying vec3 vFlowW;",
          "varying float vRiverS;",
          "#include <common>",
        ].join("\n"),
      )
      .replace(
        "#include <begin_vertex>",
        [
          "vField = field;",
          "vSlope = slope;",
          "vPpos = ppos;",
          "vNormObj = normal;",
          // World-space pieces for the river water shade (radial up + flow tangent).
          "vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;",
          "vUpW = normalize((modelMatrix * vec4(ppos, 0.0)).xyz);",
          "vFlowW = (modelMatrix * vec4(riverFlow, 0.0)).xyz;", // unit on a river, 0 off
          "vRoadSd = aRoadSd.x;",
          "vSnowRetention = aRoadSd.y;",
          "vGlacierIce = aGlacierIce;",
          "vVolcanism = aVolcanism;",
          "vDuneSand = aDuneSand;",
          ...(estuary ? ["vEstuaryShelter = aEstuaryShelter;"] : []),
          ...(butte ? ["vButteRock = aButteRock;"] : []),
          ...(canyon ? ["vCanyonSurface = aCanyonSurface;"] : []),
          ...(riverBank&&inland ? ["vRiverBankSurface = aWetlandBankSurface.zw;", "vWetlandSurface = aWetlandBankSurface.xy;"] : [
            ...(riverBank ? ["vRiverBankSurface = aRiverBankSurface;"] : []),
            ...(inland ? ["vWetlandSurface = aWetlandSurface;"] : []),
          ]),
          "vRiverS = aRiverS;", // continuous downstream arclength (m) — the flow phase
          "#include <begin_vertex>",
        ].join("\n"),
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        [
          "varying vec4 vField;",
          "varying float vSlope;",
          "varying float vRoadSd;",
          "varying float vSnowRetention;",
          "varying vec2 vGlacierIce;",
          "varying float vVolcanism;",
          "varying float vDuneSand;",
          ...(estuary ? ["varying float vEstuaryShelter;"] : []),
          ...(butte ? ["varying float vButteRock;"] : []),
          ...(canyon ? ["varying vec2 vCanyonSurface;"] : []),
          ...(riverBank ? ["varying vec2 vRiverBankSurface;"] : []),
          ...(inland ? ["varying vec2 vWetlandSurface;"] : []),
          "uniform vec3 uTable[12];",
          "uniform vec3 uCliff;",
          "uniform vec3 uSand;",
          "uniform vec3 uSnow;",
          "uniform vec3 uWater;",
          "uniform float uSeaRel;",
          "uniform float uSnowline;",
          "uniform float uAmplitude;",
          "uniform sampler2D uDetail;",
          "uniform float uHasFlora;",
          "uniform float uLivingGround;",
          "uniform vec3 uGroundKind;", // sand, mineral, ice substrate
          "varying vec3 vPpos;",
          "varying vec3 vNormObj;",
          "varying vec3 vWorldPos;",
          "varying vec3 vUpW;",
          "varying vec3 vFlowW;",
          "varying float vRiverS;",
          "uniform float uTime;",
          "uniform float uRiverDepth;",
          "uniform float uRippleFreq;",
          "uniform float uRippleSpeed;",
          "uniform float uRippleAmp;",
          "uniform vec3 uPhaseSun;",
          "uniform float uPhaseLit;",
          "uniform vec3 uPhaseColor;",
          "uniform float uPhaseEarth;",
          "uniform vec3 uPhaseEclipse;",
          WATER_UNIFORMS_GLSL,
          RIVER_SHADE_GLSL,
          GROUND_SURFACE_GLSL,
          ...(riverBank ? [RIVER_BANK_SURFACE_GLSL] : []),
          ...(butte ? [BUTTE_SURFACE_GLSL] : []),
          ...(canyon ? [CANYON_SURFACE_GLSL] : []),
          GROUND_DETAIL_GLSL,
          "vec4 triDetail(float s) {",
          "  vec3 w = abs(normalize(vPpos));",
          "  w /= (w.x + w.y + w.z);",
          "  return texture2D(uDetail, vPpos.yz * s) * w.x +",
          "         texture2D(uDetail, vPpos.xz * s) * w.y +",
          "         texture2D(uDetail, vPpos.xy * s) * w.z;",
          "}",
          ...(cloudShadowU ? [CLOUD_SHADOW_FN] : []),
          "#include <common>",
        ].join("\n"),
      )
      .replace(
        "#include <color_fragment>",
        [
          // Unlit distant river proxy, with the same restrained base as
          // the surface ribbon. rgb = water colour, a = blend mask.
          "vec4 gRiverWater = vec4(0.0);",
          "{",
          "  float relH = vField.x;",
          "  float river = vField.y;",
          "  float t01 = clamp(vField.z * 0.5 + 0.5, 0.0, 1.0);",
          "  float m01 = clamp(vField.w * 0.5 + 0.5, 0.0, 1.0);",
          "  float tf = t01 * 2.999;",
          "  float mf = m01 * 1.999;",
          "  int tr = int(floor(tf));",
          "  int mc = int(floor(mf));",
          "  float tb = fract(tf);",
          "  float mb = fract(mf);",
          "  vec3 g0 = mix(uTable[tr * 3 + mc], uTable[tr * 3 + mc + 1], mb);",
          "  vec3 g1 = mix(uTable[(tr + 1) * 3 + mc], uTable[(tr + 1) * 3 + mc + 1], mb);",
          "  vec3 ground = mix(g0, g1, tb);",
          ...(inland ? ["  vec3 nativeBiome = ground;"] : []),
          `  vec4 dFine = triDetail(${GROUND_DETAIL_SCALE.fine});`,
          `  vec4 dCoarse = triDetail(${GROUND_DETAIL_SCALE.habitat});`,
          `  vec4 dBroad = triDetail(${GROUND_DETAIL_SCALE.broad});`,
          "  if (uLivingGround > 0.5) ground = groundHabitat(ground, uSand, uCliff, vField.z, vField.w, dCoarse.b, (relH - uSeaRel) * uAmplitude);",
          "  float sandCover = 0.0;",
          "  if (uSeaRel > -100.0) {",
          "    float depthM = (uSeaRel - relH) * uAmplitude;",
          "    float dk = smoothstep(0.0, 18.0, depthM);",
          "    ground = mix(ground, uSand * vec3(0.45, 0.48, 0.55), dk);",
          "    float beach = groundBeach(-depthM, vSlope) * (1.0 - dk);",
          "    ground = mix(ground, uSand, beach);",
          ...(estuary ? ["    if (vEstuaryShelter > .12 && uLivingGround > .5) {",
          "      float mudK = groundMud(-depthM, vEstuaryShelter, vField.z, vSlope);",
          "      vec3 mud = mix(uSand * vec3(.38, .34, .28), uCliff * .50, .35) * mix(.72, 1.10, smoothstep(-.1, 1.8, -depthM));",
          "      ground = mix(ground, mud, mudK);",
          "    }"] : []),
          "    sandCover = max(beach, dk);",
          "  }",
          ...(inland ? ["  if(uLivingGround>.5) {",
          "    vec3 moss=mix(nativeBiome*vec3(.56,.65,.42),uCliff*vec3(.40,.48,.28),.35);",
          "    vec3 peat=mix(uSand*vec3(.28,.25,.18),uCliff*.32,.35);",
          "    ground=mix(ground,mix(moss,peat,clamp(vWetlandSurface.y,0.0,1.0)),clamp(vWetlandSurface.x,0.0,1.0));",
          "  }"] : []),
          "  float waterK = smoothstep(0.4, 0.75, river) * step(uSeaRel, relH);",
          "  float duneK = clamp(vDuneSand, 0.0, 1.0) * (1.0 - waterK);",
          "  ground = mix(ground, groundDuneAlbedo(uSand), duneK);",
          "  float cliffK = (1.0 - smoothstep(0.60, 0.88, vSlope)) * (1.0 - duneK);",
          "  vec3 cliffColor = mix(uCliff, vec3(.20, .235, .27), vGlacierIce.y);",
          "  ground = mix(ground, cliffColor, cliffK);",
          ...(riverBank ? ["  if(uLivingGround>.5) {",
          "    ground=groundRiverBank(ground,uSand,uCliff,vRiverBankSurface,vSlope,dCoarse.b,waterK);",
          "    float bankRock=clamp(vRiverBankSurface.y,0.0,1.0)*(1.0-waterK);",
          "    vec4 stone=triDetail(.065);",
          "    vec3 dryStone=mix(uCliff*vec3(1.08,1.00,.90),uSand*vec3(.68,.64,.58),.22);",
          "    dryStone*=.76+.50*stone.a;",
          "    ground=mix(ground,dryStone,bankRock*.82);",
          "  }"] : []),
          ...(butte ? ["  float butteK = clamp(vButteRock, 0.0, 1.0) * (1.0 - waterK);",
          "  ground = mix(ground, groundButteAlbedo(uCliff, relH * uAmplitude), butteK);"] : []),
          ...(canyon ? ["  float canyonRockK = clamp(vCanyonSurface.x, 0.0, 1.0) * (1.0 - waterK);",
          "  float canyonGroundK = clamp(vCanyonSurface.y, 0.0, 1.0) * (1.0 - waterK);",
          "  ground = mix(ground, groundCanyonSediment(uSand), canyonGroundK);",
          "  ground = mix(ground, groundCanyonAlbedo(relH * uAmplitude, dCoarse.a, dCoarse.b), canyonRockK);"] : []),
          "  float snowK = alpineSnow(vField.z, relH*uAmplitude, uSnowline*uAmplitude, vSlope, vSnowRetention, dCoarse.b)",
          "    * (1.0 - waterK) * step(uSeaRel, relH);",
          "  ground = mix(ground, uSnow, snowK);",
          "  // Quiet sand/snow, soft soil patches, broad angular mineral pigment.",
          "  float rockDetail = max(cliffK, uGroundKind.y);",
          ...(riverBank ? ["  rockDetail=max(rockDetail,clamp(vRiverBankSurface.y,0.0,1.0));", "  sandCover=max(sandCover,clamp(vRiverBankSurface.x,0.0,1.0));"] : []),
          ...(butte ? ["  rockDetail = max(rockDetail, butteK);"] : []),
          ...(canyon ? ["  rockDetail = max(rockDetail, canyonRockK);"] : []),
          "  float sandDetail = max(max(sandCover, uGroundKind.x), duneK) * (1.0 - rockDetail);",
          "  float snowDetail = max(snowK, uGroundKind.z * (1.0 - cliffK));",
          "  float detailDistance = distance(cameraPosition, vWorldPos);",
          "  float detailGain = groundDetailGain(dFine, dBroad, rockDetail, sandDetail, snowDetail, detailDistance);",
          `  ground *= 1.0 + (detailGain - 1.0) * mix(1.0, ${DUNE_DETAIL_GAIN}, duneK)${butte?` * mix(1.0, ${BUTTE_DETAIL_GAIN}, ${canyon?'butteK * (1.0 - canyonRockK)':'butteK'})`:''}${canyon?` * mix(1.0, ${CANYON_DETAIL_GAIN}, max(canyonRockK, canyonGroundK))`:''};`,
          "  vec3 basalt = mix(vec3(.017, .026, .049), vec3(.065, .072, .092), dBroad.r);",
          "  basalt *= .82 + .30 * dFine.r;",
          "  ground = mix(ground, basalt, vVolcanism);",
          "  float glacierK = smoothstep(0.15, 2.0, vGlacierIce.x);",
          "  vec3 blueIce = mix(vec3(.22, .40, .53), vec3(.56, .70, .76), smoothstep(.25, .90, vSlope));",
          "  blueIce = mix(blueIce, uSnow, smoothstep(.93, .99, vSlope) * .70);",
          "  ground = mix(ground, blueIce * (.96 + .08 * dBroad.r), glacierK);",
          "  if (uHasFlora > 0.5) {",
          // ROAD PAINT (W2d-3 wrap): roads are part of the GROUND. vRoadSd is the
          // per-vertex road-surface SDF (dist - half, metres); linear interpolation
          // keeps it a usable distance field at every LOD, so the strip stays
          // coherent from foot level to aerials. Gravel = packed-earth tint over
          // the existing detail noise; edge softens over ~1 m. Painted after biome
          // layers (a road cuts through forest floor and snow), before shadows.
          "  float roadK = (1.0 - smoothstep(-0.4, 0.9, vRoadSd)) * (1.0 - waterK);",
          "  if (roadK > 0.003) {",
          "    float roadGrain = (dFine.r - .5) * .04 * (1.0 - smoothstep(25.0, 110.0, detailDistance));",
          "    vec3 gravel = vec3(0.56, 0.485, 0.375) * (1.03 + roadGrain + (dBroad.r - .5) * .08);",
          "    ground = mix(ground, gravel, roadK);",
          "  }",
          "  }",
          // Cloud shadows: dapple the ground where the low deck sits between it + the sun.
          ...(cloudShadowU ? ["  ground *= cloudShadow(vPpos);"] : []),
          "  diffuseColor.rgb = ground;",
          "  // Distant river proxy. Flow follows the baked channel tangent.",
          "  if (waterK > 0.003) {",
          "    float ph = vRiverS * uRippleFreq - uTime * uRippleSpeed;",
          "    vec3 N = normalize(vUpW + vFlowW * (cos(ph) * uRippleAmp));",
          "    vec3 V = normalize(cameraPosition - vWorldPos);",
          "    vec3 sky = uSkyColor;",
          "    #ifdef USE_FOG",
          "      sky = fogColor;",
          "    #endif",
          "    vec4 w = riverShade(N, V, uRiverDepth, riverSky(sky), 0.0, " +
            (cloudShadowU ? "cloudShadow(vPpos)" : "1.0") +
            ");", // rivers darken under cloud shadow too
          // A distant representation only. Painting the bed underneath a live
          // ribbon made a second moving water layer visible through its edges.
          "    float farWater = smoothstep(1400.0,1900.0,distance(cameraPosition,vWorldPos));",
          "    gRiverWater = vec4(w.rgb, waterK * farWater);",
          "  }",
          "}",
          "#include <color_fragment>",
        ].join("\n"),
      )

      .replace(
        "#include <opaque_fragment>",
        [
          "outgoingLight = mix(outgoingLight, gRiverWater.rgb, gRiverWater.a);",
          // Distant-body PHASE: once this body recedes (uPhaseLit>0) shade it by ITS OWN sun
          // direction at full brightness — a Lambert terminator placed by the true sun/body
          // geometry + a faint earthshine floor — so a moon shows the correct lit fraction
          // even at the viewer's midnight (when the scene sun light is dimmed to night).
          "if (uPhaseLit > 0.001) {",
          "  float ndl = dot(normalize(vPpos), uPhaseSun);",
          // Crisp terminator (NOT pure Lambert): regolith backscatter keeps a real moon bright
          // right up to the terminator, so a crescent reads as a bright arc rather than a dim
          // graze. Flat-ish lit side with a gentle subsolar brightening for spherical roundness.
          // uPhaseEclipse dims+reddens the lit side when the body sits in its planet's umbra.
          "  float lit = smoothstep(-0.05, 0.09, ndl) * (0.6 + 0.4 * clamp(ndl, 0.0, 1.0));",
          "  vec3 phaseCol = diffuseColor.rgb * (uPhaseEarth + uPhaseColor * uPhaseEclipse * lit);",
          "  outgoingLight = mix(outgoingLight, phaseCol, uPhaseLit);",
          "}",
          "#include <opaque_fragment>",
        ].join("\n"),
      );
  };
  mat.userData.waterUniforms = waterU;
  mat.userData.cloudShadowU = cloudShadowU;
  mat.userData.phaseU = phaseU;
  // Atmosphere planets inject the cloud-shadow GLSL; airless ones don't — they need
  // DISTINCT programs (three's default cache key ignores the onBeforeCompile source).
  mat.userData.estuary = estuary;
  mat.userData.inlandWetland = inland;
  mat.userData.riverBank = riverBank;
  mat.userData.butte = butte;
  mat.userData.canyon = canyon;
  mat.customProgramCacheKey = () => (def.atmosphere ? "terrain-s06:atmo" : "terrain-s06:dry") + ":dunes1" + (estuary ? ":wetland1" : "") + (butte ? ':butte1' : '') + (canyon ? ':canyon3' : '') + (inland ? ':inland-wetland1' : '') + (riverBank ? ':river-bank2' : '');
  // Stylized: a faint rim only — no sun sheen (the ground stays matte). Ground
  // material, so it also takes the weather wet/snow aftermath.
  addStylizedShading(mat, { rim: 0.18, rimPow: 3.5, spec: 0, weatherGround: true });
  return mat;
}

/**
 * The sea is a flat-shaded sphere, so its far edge would meet the sky as a
 * hard color step — a "line" at the horizon. Real water turns mirror-like at
 * grazing angles, so we fade the surface toward the horizon sky colour (the
 * scene fog colour, which already tracks the atmosphere tint + day) as the
 * view ray grows parallel to the surface. Near water stays crisp; only the
 * distant grazing band dissolves into the sky.
 */
function makeSeaMaterial(
  color: THREE.Color,
  spaceColor: THREE.Color,
  cloudShadowU: { [k: string]: THREE.IUniform } | null,
  phaseU: { [k: string]: THREE.IUniform },
): THREE.MeshPhongMaterial {
  const mat = new THREE.MeshPhongMaterial({
    color,
    specular: new THREE.Color(0.32, 0.38, 0.45),
    shininess: 80,
    transparent: true,
    opacity: 0.95,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uHorizonPow = { value: 5.0 }; // higher = tighter band at the rim
    shader.uniforms.uHorizonStr = { value: 1.0 };
    // Ocean seen from ORBIT reads as a bright vivid blue (sky scatter + the deep water), not the
    // dark close-up "deep" tone — fade the sea albedo toward this as the body recedes (uPhaseLit).
    shader.uniforms.uSeaSpace = { value: new THREE.Vector3(spaceColor.r, spaceColor.g, spaceColor.b) };
    Object.assign(shader.uniforms, phaseU); // distant ocean-moons get the same phase shade
    if (cloudShadowU) Object.assign(shader.uniforms, cloudShadowU); // shares the terrain's block (one wire)
    // The sphere's local vertex position feeds both cloudShadow() and the phase normal.
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vSeaLocal;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvSeaLocal = position;");
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nuniform float uHorizonPow;\nuniform float uHorizonStr;\nvarying vec3 vSeaLocal;\n" +
          "uniform vec3 uPhaseSun;\nuniform float uPhaseLit;\nuniform vec3 uPhaseColor;\nuniform float uPhaseEarth;\nuniform vec3 uPhaseEclipse;\nuniform vec3 uSeaSpace;\n" +
          (cloudShadowU ? CLOUD_SHADOW_FN : ""),
      )
      .replace(
        "#include <color_fragment>",
        "#include <color_fragment>" +
          (cloudShadowU ? "\ndiffuseColor.rgb *= cloudShadow(vSeaLocal); // cloud shadows on the open sea" : ""),
      )
      .replace(
        "#include <fog_fragment>",
        /* glsl */ `{
          if (uPhaseLit > 0.001) {
            float ndl = dot(normalize(vSeaLocal), uPhaseSun);
            float lit = smoothstep(-0.05, 0.09, ndl) * (0.6 + 0.4 * clamp(ndl, 0.0, 1.0));
            vec3 phaseCol = diffuse * (uPhaseEarth + uPhaseColor * uPhaseEclipse * lit);
            // Ocean from ORBIT reads as a bright BLUE marble (Rayleigh scatter of sky + water) —
            // keep its blue HUE even under a tinted star (else a red sun crushes the blue), only
            // its brightness following the sunlit fraction. Eclipse still dims/reddens it.
            vec3 spaceLook = uSeaSpace * (0.3 + lit) * uPhaseEclipse;
            phaseCol = mix(phaseCol, spaceLook, uPhaseLit * 0.82);
            gl_FragColor.rgb = mix(gl_FragColor.rgb, phaseCol, uPhaseLit);
          }
          float ndv = abs(dot(normalize(vNormal), normalize(vViewPosition)));
          float horizon = pow(1.0 - clamp(ndv, 0.0, 1.0), uHorizonPow);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, horizon * uHorizonStr);
        }
        #include <fog_fragment>`,
      );
  };
  // Distinct program when the cloud-shadow GLSL is injected vs not (cache-key safety).
  mat.customProgramCacheKey = () => (cloudShadowU ? "sea-cs" : "sea");
  return mat;
}

/** The warm golden-hour base the sky/haze/lighting bend toward when the sun grazes
 *  the horizon. Per-planet sunset hue = this mixed with the atmosphere colour. */
export const SUNSET_WARM: readonly [number, number, number] = [1.0, 0.46, 0.18];

/**
 * Sunset strength 0..1 for a given day factor — a bump peaking at the TERMINATOR
 * (sun on the horizon, `day ≈ 0.5`) and falling to 0 at noon (`day=1`) and night
 * (`day=0`). Squared so the golden hour is concentrated, not a broad smear.
 */
export function sunsetStrength(day: number): number {
  const k = Math.max(0, 1 - 2 * Math.abs(day - 0.5));
  return k * k;
}

/** GLSL-style smoothstep: 0 below e0, 1 above e1, Hermite-eased between. */
function smoothstep01(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** Distant limbs precede local clouds; the surrounding sky haze follows them. */
export function atmosphereRenderOrder(radius: number, height: number, cameraDistance: number): number {
  return cameraDistance > radius + height * 6 ? 3.4 : 5;
}

/**
 * Atmosphere as a density gradient, not a rim "orb": the fragment shader
 * analytically intersects the view ray with the atmosphere shell and the
 * planet sphere, then converts the traversed path (weighted by an
 * exponential density falloff with altitude) into opacity. Dense and bright
 * hugging the surface, thinning smoothly into space — and because it's a
 * real volume approximation it also reads correctly from inside.
 */
export function makeAtmosphere(
  planetRadius: number,
  height: number,
  color: [number, number, number],
): THREE.Mesh & { material: THREE.ShaderMaterial } {
  const outer = planetRadius + height * 6;
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Vector3(...color) },
      uCam: { value: new THREE.Vector3(0, 0, 1e9) },
      uDay: { value: 1 },
      uFade: { value: 1 },
      uRp: { value: planetRadius },
      uRo: { value: outer },
      uH: { value: height * 1.4 },
      // Scene fog colour — what distant terrain/sea fade to. The shell converges to it
      // at the horizon so sky and ground meet seamlessly (no bright "gap" band). Updated
      // per frame from main's fog.color via StarSystem.setAtmoFog.
      uFog: { value: new THREE.Vector3(...color) },
      // DEBUG viz (StarSystem.setAtmoDebug): 0 off, 1 flat magenta (coverage/culling),
      // 2 elevation, 3 optical depth, 4 alpha.
      uDebug: { value: 0 },
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vObj;
      void main() {
        vObj = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform vec3 uCam;
      uniform float uDay;
      uniform float uFade;
      uniform float uRp;
      uniform float uRo;
      uniform float uH;
      uniform vec3 uFog;
      uniform float uDebug;
      #include <common>
      #include <logdepthbuf_pars_fragment>
      varying vec3 vObj;
      void main() {
        #include <logdepthbuf_fragment>
        vec3 ro = uCam;
        vec3 rd = normalize(vObj - uCam);
        float b = dot(ro, rd);
        float c2 = dot(ro, ro);
        // Path through the atmosphere shell...
        float discO = b * b - (c2 - uRo * uRo);
        if (discO <= 0.0) discard;
        float sq = sqrt(discO);
        float t0 = max(-b - sq, 0.0);
        float t1 = -b + sq;
        // Cut the path where the planet blocks the ray — but ONLY when viewed from OUTSIDE
        // the shell (a distant planet, whose disk must block its far-side atmosphere so the
        // limb reads as a glow, not a rim orb). When the camera is INSIDE the shell (you're
        // standing in this sky) the real terrain/sea already depth-occludes the atmosphere
        // at the true horizon, so this analytic cut is redundant — and harmful: it clips at
        // def.radius, which sits BELOW sea level, leaving a hard line that climbs over the
        // horizon at low altitude. Skip it inside.
        if (c2 > uRo * uRo) {
          float discP = b * b - (c2 - uRp * uRp);
          if (discP > 0.0) {
            float tp = -b - sqrt(discP);
            if (tp > 0.0) t1 = min(t1, tp);
          }
        }
        float path = max(t1 - t0, 0.0);
        // Density sampled at the segment midpoint's altitude.
        vec3 mid = ro + rd * (t0 + path * 0.5);
        float h = max(length(mid) - uRp, 0.0);
        float density = exp(-h / uH);
        float od = density * path / uH * 0.4;
        // The night side scatters almost nothing: the glow dies with the
        // daylight so horizons go dark and the stars own the sky.
        float alpha = (1.0 - exp(-od)) * 0.85 * mix(0.04, 1.0, uDay) * uFade;
        // The shell keeps its own colour at every angle (visible haze at the horizon).
        // The seam is closed on the GROUND side instead: distant terrain/sea fog to the
        // sky's horizon colour (see main's fog.color), so they meet seamlessly.
        vec3 col = uColor * mix(0.3, 1.0, uDay);
        if (uDebug > 0.5) {
          // Diagnostics: flat-opaque so coverage/culling and the per-pixel terms are
          // directly visible regardless of the normal alpha/colour.
          float elev = dot(rd, normalize(ro)); // ~0 horizon, →1 zenith
          if (uDebug < 1.5) { gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); return; }                                  // coverage
          if (uDebug < 2.5) { gl_FragColor = vec4(clamp(elev, 0.0, 1.0), 0.0, clamp(-elev, 0.0, 1.0), 1.0); return; } // elevation
          if (uDebug < 3.5) { gl_FragColor = vec4(clamp(od * 0.25, 0.0, 1.0), 0.0, 0.0, 1.0); return; }            // optical depth
          gl_FragColor = vec4(vec3(alpha), 1.0); return;                                                          // alpha
        }
        gl_FragColor = vec4(col, alpha);
      }
    `,
    transparent: true,
    side: THREE.BackSide,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(outer, 64, 48), mat);
  mesh.renderOrder = 5; // after the (transparent) sea
  return mesh as THREE.Mesh & { material: THREE.ShaderMaterial };
}
